// Hana — the light. One cloud of particles that becomes every image of the song.
// Every form is a pure function of song time, so seeking and replaying always land on the same picture.
(function () {
  const H = window.HANA;

  const VERT = /* glsl */`
precision highp float;
attribute vec4 aR;  // four uniform randoms
attribute vec4 aQ;  // x: index fraction, y: group (0 light, 1 you, 2 me, 3 dust)
uniform float uN, uTA, uTB, uMix, uClock, uLvl, uOn, uBeat, uPx, uGain, uSizeK, uDust, uSwirl, uHold;
uniform int uA, uB;
uniform vec4 uBand, uSeed;
#define GN 160
uniform vec4 uGlyph[GN];   // the words being written: x, y, pen down
varying vec3 vCol;

#define PI 3.14159265
#define TAU 6.28318531
const vec3 GOLD = vec3(1., .68, .34);
const vec3 ROSE = vec3(1., .42, .52);
const vec3 LAV = vec3(.6, .52, 1.);
const vec3 PEARL = vec3(.88, .9, 1.);
const vec3 TEAL = vec3(.2, .72, .9);
const vec3 DEEP = vec3(.1, .22, .7);

float MT;   // the time a form's motion runs on (song time)
float SCROLL, OPEN;   // the solo's journey: how far the land has slid past; open sea (no shore)

float h1(float n) { return fract(sin(n * 127.1 + 311.7) * 43758.5453); }
mat2 rot(float a) { float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
vec3 sph(float u, float v) { float z = u * 2. - 1.; float a = v * TAU; float r = sqrt(max(0., 1. - z * z)); return vec3(r * cos(a), r * sin(a), z); }
vec3 flow(vec3 p, float t) {
  return vec3(sin(p.y * 1.3 + t) + sin(p.z * 2.1 - t * .7),
              sin(p.z * 1.1 + t * .8) + sin(p.x * 1.7 + t * .5),
              sin(p.x * 1.4 - t * .6) + sin(p.y * 1.9 + t * .9)) * .5;
}
float tw(float seed, float t, float sp) { return pow(.5 + .5 * sin(t * sp + seed * 43.), 6.); }
float bump(float t, float a, float b, float c, float d) { return smoothstep(a, b, t) * (1. - smoothstep(c, d, t)); }
vec3 petalPal(float h) {
  h = floor(h * 5.);
  if (h < 1.) return vec3(1., .5, .62);
  if (h < 2.) return vec3(1., .78, .62);
  if (h < 3.) return vec3(.92, .9, 1.);
  if (h < 4.) return vec3(.72, .6, 1.);
  return vec3(1., .74, .38);
}
vec3 spec(float x) { return .5 + .5 * cos(TAU * (x + vec3(0., .33, .67))); }
float waveH(vec2 xz, float t) {
  return .06 * sin(xz.x * .7 + t * .9 + xz.y * .3) + .04 * sin(xz.y * 1.3 - t * 1.2) + .025 * sin((xz.x + xz.y) * 2.1 + t * 1.7);
}

// ---------------------------------------------------------------- the seed (intro, title, ending)
void fSeed(vec4 r, vec4 q, float t, out vec3 p, out vec3 c, out float s) {
  float ns = floor(uN / 36.);
  float i = floor(q.x * ns);
  float e = sqrt((i + .5) / ns);
  float R = uSeed.x;
  float th = i * 2.39996323 + MT * .07;
  float rr = e * R;
  p = vec3(rr * cos(th), rr * sin(th), 0.) + sph(r.x, r.y) * R * (.0035 + .006 * e) * sqrt(r.z);
  p.z = -rr * rr * .1 + .025 * sin(MT * 1.3 + i * .013);
  p.yz = rot(-uSeed.y) * p.yz;
  c = mix(GOLD * 1.25, mix(ROSE * .9, LAV, smoothstep(.55, 1., e)), smoothstep(0., .6, e));
  c *= (.1 + .45 * tw(h1(i), t, 1.4 + h1(i * 1.3) * 2.5)) * (1. + 1.2 * exp(-e * e * 40.));
  c *= uSeed.z * .5;
  s = .012 + .01 * (1. - e);
}

// ---------------------------------------------------------------- the sea at sunset / dawn
void fSea(vec4 r, float t, float dawn, float sunX, out vec3 p, out vec3 c, out float s) {
  float d = mix(2.2, 95., pow(r.y, 2.4));
  float halfW = 3.5 + d * .8;
  float x = (r.x - .5) * 2. * halfW;
  float z = 9. - d;
  float y = -1.4 + waveH(vec2(x, z - SCROLL), MT) * (1. + d * .015);
  p = vec3(x, y, z);
  float path = exp(-pow((x - sunX * d * .9) / (.22 + d * .085), 2.));
  float sp = tw(r.z, t, 2.5 + r.w * 7.);
  vec3 sunC = mix(vec3(1., .52, .3), vec3(1., .86, .7), dawn);
  vec3 base = mix(vec3(.2, .12, .36), vec3(.28, .32, .5), dawn);
  c = base * (.13 + .17 * sp) + sunC * path * (.06 + 1.6 * sp);
  c *= mix(1., .45, smoothstep(30., 95., d));
  s = .026 + d * .0032;
  if (r.w < .09 && OPEN < .5) {  // foam that washes the shore
    float fx = (r.x - .5) * 18.;
    float wash = .35 * sin(MT * .55 + fx * .15);
    p = vec3(fx, -1.39 + .02 * sin(fx * 3. + MT * 2.), 4.6 + wash + .06 * r.y + .3 * pow(r.z, 3.));
    c = mix(vec3(.75, .68, .86), vec3(.9, .9, 1.), dawn) * (.06 + .2 * tw(r.y, t, 3.5));
    s = .018;
  }
}

// ---------------------------------------------------------------- white threads of the dawn
void fDawn(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  if (r.w < .34) { fSea(r, t, 1., -.045, p, c, s); return; }
  float k = floor(r.x * 110.);
  float hk = h1(k + 1.), hk2 = h1(k + 7.3);
  float ang = mix(-.06, 1.06, (k + .5) / 110.) * PI + (hk - .5) * .04;
  vec3 S = vec3(-3.6, -1.15, -72.);
  float u = fract(r.y + MT * (.012 + .02 * hk2));
  vec3 E = vec3(cos(ang) * (15. + 10. * hk), -1.3 + max(sin(ang), -.05) * (10. + 7. * hk2), 6.5);
  vec3 b = mix(S, E, u);
  b.y += sin(u * 5. + MT * .35 + k) * .35 * u;
  p = b + (vec3(r.z, fract(r.z * 7.13), fract(r.z * 3.7)) - .5) * .025 * (1. + u * 5.);
  float fade = smoothstep(0., .04, u) * (1. - smoothstep(.55, .95, u));
  c = mix(vec3(1., .78, .5) * 1.6, PEARL, smoothstep(0., .3, u)) * fade * (.14 + .3 * tw(r.z, t, 1.7));
  s = .018 + .03 * u;
}

// ---------------------------------------------------------------- a perfect world: earth and her moon
// a steady orbit (the moon tidally locked); a storm of meteors whose paths bend in the earth's gravity
// (worked out on the CPU, see buildMeteors): the moon takes many, some strike the earth, some miss;
// then the moon's orbit decays, it crashes into the earth, both shatter, and the pieces fall together as one new world
const vec3 EARTH = vec3(0., .3, 0.);
const vec3 SUNL = vec3(-.72, .3, .62);      // where the sunlight comes from
const float RE = 1.25, RM = .36, RO = 3.;   // earth radius, moon radius, orbit radius
const float W0 = .42;                       // the moon's angular speed on its steady orbit
const float TFALL = 59.4, TIMPACT = 63.3;   // the orbit slowly gives way, then they collide (on "shatter")
uniform sampler2D uMet;                     // meteor paths: 64 samples per row, then (t0, dt, tEnd, kind), (normal, big)
uniform vec4 uMoonHit[24], uEarthHit[16];   // craters: local normal, impact time (negative: a big one)
float decay(float t) { return pow(clamp((t - TFALL) / (TIMPACT - TFALL), 0., 1.), 2.4); }   // slow to start, then all at once
float orbitR(float t) { return mix(RO, RE + RM * .6, decay(t)); }
float orbitW(float t) { float e = decay(t); return W0 * pow(RO / orbitR(t), 1.5) * (1. + 3. * e * e); }   // Kepler, and then some: it whirls in
float orbitA(float t) {
  float a = W0 * t;
  if (t > TFALL) {
    float dt = (min(t, TIMPACT) - TFALL) / 24.;
    for (int i = 0; i < 24; i++) { float tt = TFALL + (float(i) + .5) * dt; a += (orbitW(tt) - W0) * dt; }
  }
  return a;
}
vec3 orbitN() { return normalize(vec3(-.22, 1., .1)); }
vec3 moonPos(float t) {
  vec3 N = orbitN(), X = normalize(cross(N, vec3(0., 0., 1.))), Y = cross(N, X);
  float a = orbitA(t);
  return EARTH + (X * cos(a) + Y * sin(a)) * orbitR(min(t, TIMPACT));
}
vec3 rodr(vec3 v, vec3 k, float a) { return v * cos(a) + cross(k, v) * sin(a) + k * dot(k, v) * (1. - cos(a)); }
vec3 moonWorld(vec3 l, float t) { return rodr(l, orbitN(), orbitA(t)); }
vec3 earthWorld(vec3 l, float t) { vec3 n = l; n.xz = rot(t * .1) * n.xz; return n; }
vec4 metTex(float col, float k) { return texture2D(uMet, vec2((col + .5) / 66., (k + .5) / 44.)); }
vec3 metPos(float k, float tt) {
  vec4 info = metTex(64., k);
  float f = clamp((tt - info.x) / info.y, 0., 63.);
  float i = floor(f);
  return mix(metTex(i, k).xyz, metTex(min(i + 1., 63.), k).xyz, f - i);
}
float fbm3(vec3 n) {
  return sin(n.x * 3.1 + 1.) * sin(n.y * 2.7) * sin(n.z * 3.3 + 2.) + .5 * sin(n.x * 6.3 + n.y * 2.1) * sin(n.z * 5.7 - 1.3)
       + .25 * sin(n.y * 12.7 + n.z * 3.) * sin(n.x * 11.3 + 1.7) + .12 * sin(n.x * 23. + n.z * 19.) * sin(n.y * 21. - 2.);
}
// after the crash: every piece flies out from the point of impact, slows, and falls back into one new world
// after the crash: every piece is thrown out from the point of impact, tumbling, and keeps going
vec3 shatter(vec3 p0, vec4 r, float sh, float speed, out float heat) {
  vec3 hn = normalize(moonPos(TIMPACT - .01) - EARTH);
  vec3 I = EARTH + hn * RE;
  vec3 d = normalize(p0 - I + sph(r.z, fract(r.x * 37.)) * .6 + 1e-4);
  float v = speed * (1. + 2.5 * pow(fract(r.y * 29.), 2.));
  heat = exp(-sh * 1.1) * (1. + 2. * exp(-length(p0 - I) * 1.5));
  return p0 + d * v * (1. - exp(-sh * 2.)) * 1.8 + d * v * sh * .25 + flow(p0 * 1.3, sh) * sh * .15;
}
void fWorld(vec4 r, vec4 q, float t, out vec3 p, out vec3 c, out float s) {
  vec3 L = normalize(SUNL);
  float tc = min(t, TIMPACT);               // the bodies as they were at the moment of the crash
  float sh = max(t - TIMPACT, 0.);
  vec3 M = moonPos(tc);
  float sel = r.w;
  s = .018;
  if (sel < .46) {                          // the earth
    vec3 n0 = sph(r.x, r.y);
    vec3 n = earthWorld(n0, MT);
    bool cloud = fract(r.z * 7.3) < .2;
    float f = fbm3(n0);
    float land = smoothstep(.1, .2, f + .05);
    float lat = abs(n0.y);
    vec3 ocean = mix(vec3(.015, .05, .16), vec3(.04, .16, .3), smoothstep(-.1, .1, f));
    vec3 ground = mix(vec3(.1, .2, .07), vec3(.36, .28, .15), smoothstep(-.3, .5, sin(n0.x * 4. + n0.z * 2.) + .4 * f));
    ground = mix(ground, vec3(.62, .5, .32), smoothstep(.15, .5, .4 - abs(n0.y - .05) + .2 * sin(n0.x * 5.)));
    vec3 col = mix(ocean, ground, land);
    col = mix(col, vec3(.86, .9, .95), smoothstep(.8, .88, lat + .04 * f));
    // the meteors that got through: scorched, glowing craters
    float dark = 0., heatE = 0.;
    for (int i = 0; i < 16; i++) {
      vec4 h = uEarthHit[i];
      float T = abs(h.w);
      if (t < T) continue;
      float d = length(n0 - h.xyz) / (h.w < 0. ? .3 : .2);
      if (d < 1.) { dark = max(dark, 1. - d); heatE = max(heatE, (1. - d) * exp(-(t - T) * .5)); }
    }
    col *= 1. - .7 * dark;
    float nl = dot(n, L);
    float day = smoothstep(-.05, .22, nl);
    vec3 V = normalize(cameraPosition - (EARTH + n * RE));
    float spec = pow(max(dot(reflect(-L, n), V), 0.), 36.) * (1. - land) * day * (1. - dark);
    float rim = pow(1. - abs(dot(n, V)), 3.);
    c = col * (.03 + 1.1 * day) + vec3(1., .5, .25) * .25 * smoothstep(-.12, 0., nl) * (1. - smoothstep(0., .14, nl))
      + vec3(1., .9, .75) * spec * .9 + vec3(1., .4, .12) * heatE * 1.3;
    c += vec3(1., .72, .38) * .55 * land * (1. - day) * (1. - dark) * step(.955, h1(floor(r.x * 1400.) * 7. + floor(r.y * 700.)));
    // the falling moon raises a tide: the side facing it swells and glows
    vec3 toM = normalize(M - EARTH);
    float tide = decay(tc) * pow(max(dot(n, toM), 0.), 3.);
    p = EARTH + n * RE * (1. + .12 * tide);
    c += vec3(1., .55, .3) * tide * .25;
    s = .026;
    if (cloud) {
      float cl = smoothstep(.15, .7, fbm3(n0 * 1.6 + vec3(MT * .02, 0., 0.)) + .2) * (1. - dark);
      p = EARTH + n * (RE + .03);
      c = vec3(.92, .94, 1.) * cl * (.02 + .45 * day);
      s = .034;
    }
    float front = smoothstep(-.03, .1, dot(n, V));
    if (fract(r.z * 3.1) < .07) {
      p = EARTH + n * (RE + .06);
      c = vec3(.3, .55, 1.) * .35 * rim * (.15 + day);
      front = 1.;
    }
    c *= .95 * front;
    if (sh > 0.) {                          // the crash
      float heat;
      vec3 pp = shatter(p, r, sh, 1.6, heat);
      c = (c / max(front, .3) * .6 + vec3(1., .45, .18) * heat * .5) * exp(-sh * .5);
      p = pp;
    }
  } else if (sel < .64) {                   // the moon
    vec3 nl0 = sph(r.x, r.y);
    float maria = smoothstep(.1, .5, sin(nl0.x * 2.3 + 1.) * sin(nl0.y * 2.9 - .5) * sin(nl0.z * 2.1 + 2.) + .2);
    float small = smoothstep(.75, .95, abs(sin(nl0.x * 17.) * sin(nl0.y * 15.) * sin(nl0.z * 19.)));
    float rad = RM, dark = 0., heat = 0., blown = 0.;
    vec3 blowDir = vec3(0.);
    for (int i = 0; i < 24; i++) {
      vec4 h = uMoonHit[i];
      float T = abs(h.w);
      if (t < T) continue;
      bool big = h.w < 0.;
      float cr = big ? .6 : .36;
      float d = length(nl0 - h.xyz) / cr;
      if (d < 1.) {
        rad -= RM * .24 * (1. - d * d) * smoothstep(0., .15, t - T);
        dark = max(dark, 1. - d);
        heat = max(heat, (1. - d) * exp(-(t - T) * .6));
        if (big && d < .7 && h1(T + floor(r.x * 300.)) < .85) { blown = max(blown, smoothstep(0., 2.5, t - T)); blowDir = h.xyz; }
      } else if (d < 1.25) rad += RM * .05 * (1.25 - d) * smoothstep(0., .2, t - T);
    }
    vec3 nw = moonWorld(nl0, tc);
    // near the end, the earth's tides stretch it toward her
    vec3 toE = normalize(EARTH - M);
    float stretch = smoothstep(2.3, 1.7, length(M - EARTH));
    vec3 off = nw * rad;
    off += toE * dot(off, toE) * stretch * .7;
    p = M + off;
    float day = smoothstep(-.04, .18, dot(nw, L));
    float mfront = smoothstep(-.03, .1, dot(nw, normalize(cameraPosition - M)));
    vec3 col = mix(vec3(.62, .62, .6), vec3(.3, .3, .32), maria) * (1. - .25 * small) * (1. - .55 * dark);
    float fall = decay(tc);                 // as it loses its orbit it glows from the strain, more and more
    c = (col * (.015 + .55 * day) * .5 + vec3(1., .42, .15) * (heat * 1.4 + fall * (.4 + .5 * tw(r.z, t, 6.)) * (1. - .5 * mfront))) * mfront;
    s = .022;
    if (fract(r.z * 13.) < fall * .5) {     // and sheds a trail of itself along the path it's whirling down
      float lag = fract(r.z * 29.) * .5;
      p = moonPos(tc - lag) + sph(r.x, r.y) * RM * (.6 + lag * 1.5);
      c = vec3(1., .55, .25) * .35 * (1. - lag * 1.6) * fall;
    }
    if (blown > 0.) {
      vec3 bw = moonWorld(blowDir, tc);
      p += (bw * 1.6 + sph(r.z, fract(r.y * 7.)) * .7) * blown * (.6 + h1(r.x * 91.));
      c = mix(c, vec3(.5, .45, .42) * .2 + vec3(1., .4, .15) * .3 * (1. - blown), blown);
    }
    if (sh > 0.) {
      float heatS;
      vec3 pp = shatter(p, r, sh, 2.6, heatS);
      c = (c / max(mfront, .3) * .6 + vec3(1., .45, .18) * heatS * .6) * exp(-sh * .5);
      p = pp;
    }
  } else if (sel < .78) {                   // the meteors: burning heads and trails, on paths bent by the earth
    float k = floor(r.x * 44.);
    vec4 info = metTex(64., k);
    float part = fract(r.x * 44.);
    float big = metTex(65., k).w;
    float tt = t;
    if (part >= .3) tt = t - (part - .3) / .7 * .5;   // the trail is where the rock was a moment ago
    p = metPos(k, tt);
    float alive = step(info.x, tt) * step(t, info.z);
    if (part < .3) {
      p += sph(r.y, r.z) * (.07 + .09 * big) * pow(fract(r.w * 13.), .3);
      c = vec3(1., .85, .65) * (1. + big);
    } else {
      float tr = (part - .3) / .7;
      p += sph(r.y, r.z) * .05 * tr * (1. + 2. * tr);
      c = mix(vec3(1., .7, .35), vec3(.9, .25, .12), tr) * .9 * pow(1. - tr, 1.4) * (1. + big);
    }
    c *= alive * smoothstep(0., .3, t - info.x);
    s = .022;
  } else if (sel < .92) {                   // impacts on the moon and the earth: a flash and a fountain of debris
    float k = floor(r.x * 44.);
    vec4 info = metTex(64., k), hit = metTex(65., k);
    float a = t - info.z;
    float part = fract(r.x * 44.);
    float big = hit.w;
    vec3 nw = info.w > 1.5 ? moonWorld(hit.xyz, tc) : earthWorld(hit.xyz, t);
    vec3 P = info.w > 1.5 ? M + nw * RM : EARTH + nw * RE;
    if (part < .25) {
      p = P + sph(r.y, r.z) * (.1 + .8 * (1. - exp(-a * 6.))) * (1. + big) * pow(fract(r.w * 17.), .5);
      c = vec3(1., .9, .75) * 2.6 * exp(-a * 6.) * (1. + big);
    } else {
      vec3 d = normalize(nw * 1.3 + sph(r.y, r.z));
      float sp = (1. + 3. * fract(r.w * 23.)) * (1. + big);
      p = P + d * sp * (1. - exp(-a * 2.)) * .8 - normalize(P - EARTH) * a * a * .15;   // falling back
      c = mix(vec3(1., .75, .4), vec3(.55, .45, .4), smoothstep(0., 1.2, a)) * .8 * exp(-a * 1.);
    }
    c *= step(.5, info.w) * step(0., a) * step(a, 3.5) * (1. - smoothstep(TIMPACT - .2, TIMPACT, t));
    s = .02;
  } else {                                  // stars; and the great flash and splash of the crash
    p = vec3((r.x - .5) * 40., (r.y - .5) * 22., -12. - r.z * 10.);
    c = PEARL * .12 * tw(r.z, t, 1.3);
    s = .016;
    if (fract(r.w * 9.) < .6 && sh > 0.) {
      vec3 hn = normalize(moonPos(TIMPACT - .01) - EARTH);
      vec3 I = EARTH + hn * RE;
      vec3 d = normalize(hn * .6 + sph(r.x, r.y));
      float far = (1. - exp(-sh * 2.2)) * (1.5 + 5. * r.z * r.z);
      p = I + d * far;
      c = mix(vec3(1., .9, .7), mix(vec3(1., .45, .2), ROSE, r.z), smoothstep(0., .4, sh)) * (.25 + 1.3 * exp(-sh * 6.)) * exp(-sh * 1.3);
    }
  }
}
// ---------------------------------------------------------------- a thousand sunflowers (and letting them go)
void fFlowers(vec4 r, float t, float tb, float tl, out vec3 p, out vec3 c, out float s) {
  // a field of sunflowers on stems; nearer flowers get more grains
  float qf = pow(r.x, 2.);
  float f = floor(qf * 380.);
  float hf = h1(f * 1.7 + .3), hg = h1(f * 3.1 + .7), hh = h1(f * 5.3 + .1);
  float d = mix(4.6, 60., pow((f + hf) / 380., 1.1));
  float x = (hg - .5) * 2. * (2.6 + d * .85);
  float near = 1.;
  if (SCROLL > 0.) { d = 4.6 + mod(d - 4.6 - SCROLL, 55.4); near = smoothstep(4.6, 8., d); }   // the field slides past
  float z = 9. - d;
  float gk = 1. + d * .012;                                      // far ones a little larger, to read
  vec3 B = vec3(x, -2.35 + .14 * sin(x * .4 + z * .3), z);
  float Hs = (1.05 + .45 * hh) * gk;
  float sway = .05 * sin(MT * .7 + f) * gk;
  vec3 C = B + vec3(sway, Hs, 0.);
  float R = (.15 + .05 * hh) * gk;                               // the disk
  float open = smoothstep(0., .9, t - (tb + hf * .45 + hg * .9));   // they burst open on the downbeat
  vec3 ez = normalize(vec3(-x * .02, -.28 - .1 * hh + .2 * open, 1.));   // the face, nodding a little
  vec3 ex = normalize(cross(vec3(0., 1., 0.), ez)), ey = cross(ez, ex);
  vec3 lp;
  if (r.z < .16) {                                               // the stem, and two leaves
    float v = r.y;
    vec3 st = mix(B, C - ez * R * .3, v) + vec3(sin(PI * v) * .06 * (hh - .5) * gk, 0., 0.);
    p = st + sph(r.w, fract(r.z * 37.)) * .012 * gk;
    c = vec3(.42, .6, .24) * .35;
    if (fract(r.z * 7.) < .45) {                                 // a broad leaf off the stem
      float side = step(.5, fract(r.z * 19.)) * 2. - 1.;
      float lv = .38 + .22 * step(0., side);
      float u = fract(r.w * 3.1), wv = fract(r.w * 11.3) * 2. - 1.;
      float L = .42 * gk, W = .2 * gk * sin(PI * pow(u, .7)) * wv;
      vec3 at = mix(B, C, lv);
      p = at + vec3(side * (u * L), u * L * .35 - u * u * L * .4, W);
      c = vec3(.4, .62, .24) * .3 * (.6 + .6 * pow(abs(wv), 3.));
    }
    c *= (.5 + .5 * tw(r.w, t, 1.4)) * mix(1., .4, smoothstep(22., 60., d)) * near;
    s = .012 + d * .0012;
  } else {
    if (r.z < .46) {                                             // the seed disk: a golden-angle spiral
      float i = floor(r.y * 160.);
      float e = sqrt((i + .5) / 160.);
      float th = i * 2.39996323;
      lp = vec3(cos(th), sin(th), 0.) * e * R * (.55 + .45 * open);
      lp.z = .03 * R * (1. - e * e);
      c = mix(vec3(.3, .15, .06) * .45, vec3(1., .55, .16) * 1.1, smoothstep(.72, .95, e)) * (.6 + .6 * tw(i + f, t, 2.2));
    } else {                                                     // the ray petals, two rings
      float k = floor(r.y * 26.);
      float ring = mod(k, 2.);
      float a = (k + .5 * ring) / 26. * TAU + hh;
      float v = fract(r.y * 26.);
      float u = fract(r.w * 7.7) * 2. - 1.;
      u = sign(u) * (1. - pow(1. - abs(u), 4.));    // grains crowd the petal's outline
      float L = R * mix(.9, 1.15, h1(k + f)) * (1. - .12 * ring);
      float fold = mix(1.35, .12 + .1 * ring, open);            // closed: wrapped forward around the disk
      vec3 dir = vec3(cos(a), sin(a), 0.), tg = vec3(-sin(a), cos(a), 0.);
      vec3 out_ = dir * cos(fold) + vec3(0., 0., 1.) * sin(fold);
      float w = R * .22 * sin(PI * pow(v, .55)) * (1. - .35 * v);
      lp = dir * R * .95 + out_ * v * L + tg * u * w;
      lp.z -= v * v * .08 * R;
      c = mix(vec3(1., .6, .1), vec3(1., .8, .22), v) * 1.3 * (.45 + .8 * pow(abs(u), 3.));
    }
    p = C + ex * lp.x + ey * lp.y + ez * lp.z;
    c *= (.18 + .26 * tw(r.w, t, 1.8)) * (.45 + .55 * open) * near;
    c *= mix(1., .45, smoothstep(22., 60., d));
    s = .013 + d * .0013;
  }
  // a million reasons: motes rise out of the hearts
  float pol = step(.84, r.w) * smoothstep(tb + 4.6, tb + 6.2, t) * step(tl, 9000.);
  if (pol > 0.) {
    float age = fract(r.y * 3.7 + (t - tb) * (.08 + .05 * r.z));
    vec3 pp = C + vec3(0., age * 3.2, 0.) + flow(C + age * 2., MT * .5) * age * .7;
    p = mix(p, pp, pol);
    c = mix(c, GOLD * (.5 * (1. - age)) * tw(r.z, t, 4.), pol);
  }
  // letting go: petals lift away and drift up into the sky
  float lift = smoothstep(tl + hf * 3., tl + 2.5 + hf * 4., t) * step(.46, r.z);   // the petals, not the stems
  if (lift > 0.) {
    float a = t - tl - hf * 3.;
    vec3 up = vec3(sin(a * .5 + f) * 1.2 + a * .25, a * (.9 + hg * 1.2), -a * .3);
    up += flow(p * .3 + f, MT * .4) * .6 * lift;
    p += up * lift;
    c *= 1. - .35 * lift;
  }
}

// ---------------------------------------------------------------- a billion memories: neurons and their synapses
const float NN = 34.;
vec3 soma(float n) {
  return vec3(0., .5, -4.) + vec3((h1(n * 1.1) - .5) * 11., (h1(n * 1.3 + .2) - .5) * 5.5, (h1(n * 1.7 + .5) - .5) * 8.);
}
float nTarget(float n) { return mod(n + 1. + floor(h1(n * 5.3) * 5.), NN); }
vec3 axonPt(float n, float u) {             // a curved axon from neuron n to the one it speaks to
  vec3 A = soma(n), B = soma(nTarget(n));
  vec3 C = (A + B) * .5 + (vec3(h1(n * 9.1), h1(n * 13.3), h1(n * 7.7)) - .5) * 3.;
  return mix(mix(A, C, u), mix(C, B, u), u);
}
float nSpeed(float n) { return .7 + .6 * h1(n * 2.9); }
void fGalaxy(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float n = floor(r.x * NN);
  float sub = fract(r.x * NN);
  vec3 S0 = soma(n);
  float sel = r.w;
  s = .018;
  if (sel < .07) {                          // the cell body
    p = S0 + sph(r.y, r.z) * .16 * pow(sub, .4) * (1. + .15 * sin(MT * 2. + n));
    c = mix(GOLD * 1.2, ROSE, sub) * .5 * (.6 + .4 * tw(n, t, 1.5));
    s = .022;
  } else if (sel < .5) {                    // dendrites, branching
    float b = floor(sub * 7.);
    vec3 d0 = normalize(sph(h1(n * 7. + b), h1(n * 11. + b * 3.)));
    vec3 sd = normalize(cross(d0, vec3(.3, 1., .2)));
    float L = .9 + .7 * h1(n * 3. + b * 5.);
    float u = r.y;
    vec3 P;
    if (r.z < .55) {
      P = S0 + d0 * u * L + sd * sin(u * 3. + b) * .18 * u;
    } else {                                // a fork off the main branch
      float u0 = .3 + .45 * fract(r.z * 3.7);
      vec3 base = S0 + d0 * u0 * L + sd * sin(u0 * 3. + b) * .18 * u0;
      vec3 d1 = normalize(d0 + sph(h1(n + b + u0 * 7.), h1(n * 2. + b + u0)) * .9);
      P = base + d1 * u * L * .55 * (1. - u0 * .5);
      u = u0 + u * (1. - u0);
    }
    p = P + sph(fract(r.z * 13.), fract(r.y * 17.)) * .012 * (1. - u);
    c = mix(ROSE, LAV, u) * .16 * (1. - .6 * u) * (.7 + .3 * tw(r.y + n, t, 2.));
  } else if (sel < .86) {                   // the axon, with signals racing along it
    float u = r.y;
    p = axonPt(n, u) + sph(r.z, fract(sel * 13.)) * .01;
    float ph = fract(u * 1.5 - MT * nSpeed(n) + h1(n * 3.));
    float pulse = exp(-pow((ph - .5) * 14., 2.));
    c = mix(LAV * .08, GOLD * 1.6, pulse) * (.6 + .4 * tw(r.z, t, 3.));
    s = .018 + .012 * pulse;
  } else if (sel < .94) {                   // the synapse: a bulb, sparking as each signal arrives
    vec3 B = soma(nTarget(n));
    vec3 end = axonPt(n, .93);
    float arrive = fract(1.5 * .93 - MT * nSpeed(n) + h1(n * 3.));
    float fire = exp(-pow((arrive - .5) * 6., 2.));
    p = end + sph(r.y, r.z) * (.05 + .25 * fire * pow(fract(sel * 31.), .5));
    c = mix(ROSE, GOLD, fire) * (.15 + 1.2 * fire);
    s = .02;
  } else {                                  // memories drifting between them
    p = vec3(0., .5, -4.) + (vec3(r.x, r.y, r.z) - .5) * vec3(16., 9., 12.) + flow(vec3(r.x, r.y, r.z) * 6., MT * .3) * .3;
    c = mix(LAV, PEARL, r.z) * .08 * tw(r.y, t, 1.6);
  }
}

// ---------------------------------------------------------------- ink made of oceans
// the pen's path through the written words (from js/glyph.js), and whether the pen is down there
// find the segment of the pen's path that progress u falls in (the points carry their progress in w)
float glyphSeg(float u, out float f) {
  float lo = 0., hi = float(GN - 1);
  for (int k = 0; k < 8; k++) {
    float mid = floor((lo + hi) * .5);
    if (uGlyph[int(mid)].w <= u) lo = mid; else hi = mid;
  }
  lo = min(lo, float(GN - 2));
  vec4 a = uGlyph[int(lo)], b = uGlyph[int(lo) + 1];
  f = clamp((u - a.w) / max(b.w - a.w, 1e-5), 0., 1.);
  return lo;
}
vec2 glyphPt(float u) {
  float f; int i = int(glyphSeg(u, f));
  return mix(uGlyph[i].xy, uGlyph[i + 1].xy, f);
}
float glyphDown(float u) {        // 1 on a stroke, .5 on the thin cursive link between strokes
  float f; int i = int(glyphSeg(u, f));
  return uGlyph[i + 1].z;
}
vec3 inkPath(float u, float mode) {
  vec2 g = glyphPt(u);
  if (mode < .5) return vec3(g.x, g.y + .35, .25 * sin(u * TAU * 2.));   // 하나, in the ocean's ink
  return vec3(g.x, g.y + .25, 0.);                                     // ONE
}
void fInk(vec4 r, float t, float t0, float t1, float mode, out vec3 p, out vec3 c, out float s) {
  float head = smoothstep(t0, t1, t);
  if (r.w < .64) {
    float u = r.x * head;
    float age = (head - u) * (t1 - t0) + max(t - t1, 0.);
    vec3 P = inkPath(u, mode);
    vec3 T = normalize(inkPath(u + .002, mode) - P + 1e-5);
    vec3 N = vec3(-T.y, T.x, 0.);
    float wgt = glyphDown(u);
    float lk = wgt * 2. - 1.;                 // 0 on a cursive link, 1 on a stroke
    float spread = ((.04 + .1 * abs(T.y)) + age * .07) * mix(.14, 1., lk);
    p = P + N * (r.y - .5) * 2. * spread + vec3(0., 0., (r.z - .5) * spread);
    p += flow(P * 1.4 + r.xyz * 2., MT * .6) * min(age, 4.) * .06 * mix(.3, 1., lk);
    c = mix(vec3(.75, .96, 1.) * 1.3, mix(TEAL, DEEP, smoothstep(.5, 3.5, age)), smoothstep(0., .5, age));
    c *= (.22 + .2 * tw(r.z, t, 2.)) * smoothstep(0., .03, head) * mix(.13, 1., lk);
    s = .02;
  } else {   // the ocean the ink is drawn from
    vec3 o = vec3((r.x - .5) * 20., -2.6 + (r.y - .5) * 1.1, (r.z - .5) * 10. - 2.5);
    o.y += waveH(o.xz, t) * 4.;
    o += flow(o * .35, MT * .6) * .28;
    p = o;
    c = mix(DEEP, TEAL, r.y) * (.07 + .1 * tw(r.z, t, 1.5));
    s = .03;
  }
}

// ---------------------------------------------------------------- a million goodbyes: stars going supernova
// a chain reaction: each blast's shock front reaches the next star just as it goes, and they come faster and faster
const float SN = 13.;
float snT(float k) { return 81.6 + .58 * (1. - pow(.85, k)) / .15; }
const float SND = 2.1;                      // how far the shock carries to the next star
float snRmax(float k) { return 2.5 + .8 * k / SN + .2 * h1(k * 5.1); }
float snR(float k, float a) {               // fast enough to reach the next star exactly when it goes
  float dt = snT(min(k, SN - 2.) + 1.) - snT(min(k, SN - 2.));
  float lam = -log(1. - SND / snRmax(k)) / dt;
  return snRmax(k) * (1. - exp(-a * lam)) + .06 * a;
}
vec3 snC(float k) {
  vec3 O = vec3(-5.2, -.6, -1.5);
  for (int i = 0; i < 13; i++) {
    float fi = float(i);
    if (fi >= k) break;
    float ang = .35 + 1.3 * sin(fi * 1.9) ;
    vec3 d = normalize(vec3(cos(ang), sin(ang) * .8, .3 * sin(fi * 1.7)) - O * vec3(.05, .16, .1));
    O += d * SND;
  }
  return O;
}
void fBye(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  s = .018;
  if (r.w > .92) {                          // the sky they belong to
    p = vec3((r.y - .5) * 34., (r.z - .5) * 18., -10. - fract(r.w * 7.) * 8.);
    c = PEARL * .1 * tw(r.z, t, 1.3);
    return;
  }
  float k = floor(r.x * SN);
  float a = MT - snT(k);
  vec3 O = snC(k);
  float part = r.w / .92;
  vec3 dir = sph(r.y, r.z);
  if (a < 0.) {                             // a star, flaring as the shock front reaches it
    float pre = smoothstep(-.5, 0., a);
    p = O + dir * .045 * (1. - .6 * smoothstep(-.3, 0., a)) * sqrt(fract(r.w * 13.));
    c = mix(vec3(1., .8, .6), PEARL, pre) * (.15 + .5 * pre) * step(part, .08) * (.7 + .3 * tw(k, t, 6.));
    return;
  }
  float R = snR(k, a);
  float heat = 1.3 + 1.5 * k / SN;          // each one fiercer than the last
  float in_ = smoothstep(0., .2, a);
  float flash = exp(-a * 3.5);
  // colour by region of the sky it flies into (hydrogen rose, oxygen teal), not grain by grain
  float hue = smoothstep(-.4, .4, sin(dot(dir, vec3(1.3, 2.1, .7)) * 1.7 + k * 1.9));
  vec3 gas = mix(TEAL, ROSE, hue);
  if (part < .5) {                          // the shell, torn into bright filaments
    float kn = .5 + .5 * sin(dot(dir, vec3(5.1, 7.3, 3.7)) + k * 2.) * sin(dot(dir, vec3(-3.3, 6.1, 8.2)) + k);
    kn = pow(kn, 5.);
    float rad = R * (.86 + .14 * kn + .03 * (fract(r.w * 17.) - .5));
    p = O + dir * rad;
    c = mix(gas, GOLD * 1.1, kn * .5) * (.22 + 2.6 * kn) * (.35 + 2. * exp(-a * 1.3)) * exp(-a * .45) * in_;   // blazing when fresh
    s = .022;
  } else if (part < .64) {                  // fingers of debris reaching past the shell
    float j = floor(fract(r.w * 31.) * 90.);
    vec3 jd = normalize(sph(h1(j + k * 71.), h1(j * 1.3 + k * 17.)) + sph(r.y, r.z) * .03);
    float along = mix(.82, 1.25 + .2 * h1(j * 2.9 + k), pow(fract(r.w * 53.), .7));
    p = O + jd * along * R;
    float hj = smoothstep(-.4, .4, sin(dot(jd, vec3(1.3, 2.1, .7)) * 1.7 + k * 1.9));
    c = mix(TEAL, ROSE, hj) * 1.5 * (.6 - .35 * smoothstep(.85, 1.4, along)) * (.35 + 2. * exp(-a * 1.3)) * exp(-a * .5) * in_;
  } else if (part < .74) {                  // hot ejecta inside, dim
    float rad = R * .75 * pow(fract(r.w * 7.), .5);
    p = O + dir * rad + flow(dir * 2. + k, MT * .5) * .06 * R;
    c = mix(GOLD, gas, rad / max(R, 1e-3)) * .1 * exp(-a * .55) * in_;
  } else if (part < .86) {                  // the ring a shockwave throws off
    float an = r.y * TAU;
    vec3 q = vec3(cos(an), sin(an) * .12 * (fract(r.z * 5.) - .5), sin(an));
    q.xy = rot(h1(k * 9.1) * 2. - 1.) * q.xy;
    q.yz = rot(.5 + h1(k * 2.3)) * q.yz;
    p = O + q * R * 1.22;
    c = mix(PEARL, TEAL, .4) * .5 * exp(-a * .9) * in_;
  } else if (part < .95) {                  // the flash throws out spikes of light
    float sp = floor(fract(r.w * 41.) * 6.);
    float an = sp / 6. * PI + h1(k * 3.9) * PI;
    float u = fract(r.w * 59.);
    float L = (1.2 + 4.5 * (1. - flash)) * (mod(sp, 2.) < .5 ? 1. : .55) * snRmax(k) * .45;
    p = O + vec3(cos(an), sin(an), 0.) * (u - .5) * 2. * L;
    c = mix(PEARL, GOLD, .25) * 2.2 * flash * pow(1. - abs(u - .5) * 2., 2.) * smoothstep(0., .03, a);
    s = .02;
  } else {                                  // the flash, and the small hot heart left behind
    p = O + dir * (.04 + 1.6 * flash * sqrt(fract(r.w * 23.)));
    c = mix(vec3(.8, .9, 1.), mix(PEARL, GOLD, .3), flash) * (.06 + 1.5 * flash) * (.7 + .3 * tw(r.y, t, 9.));
    s = .02;
  }
  c *= (1. + 1.8 * flash) * heat;
}

// ---------------------------------------------------------------- a billion sorries (rain); dir -1 sends it back up
void fRain(vec4 r, float t, float dir, out vec3 p, out vec3 c, out float s) {
  if (r.w < .55) {
    float lane = floor(r.x * 1100.);
    vec2 xz = vec2((h1(lane) - .5) * 18., 1.5 - h1(lane * 1.7) * 16.);
    float sp = .9 + .5 * h1(lane * 3.3);
    float ph = fract(h1(lane * 7.7) + MT * sp * .32 * dir);
    float y = mix(5.5, -1.4, ph);
    y += (r.y - .5) * .22;
    p = vec3(xz.x, y, xz.y);
    c = mix(vec3(.55, .65, 1.), PEARL, r.z) * (.08 + .07 * r.z);
    s = .016;
  } else {
    float k = floor(r.x * 160.);
    vec2 cxz = vec2((h1(k) - .5) * 18., 1. - h1(k * 2.3) * 16.);
    float age = fract(h1(k * 5.1) + MT * .42);
    if (dir < 0.) age = 1. - age;
    float rad = age * 1.5;
    float a = r.y * TAU;
    p = vec3(cxz.x + cos(a) * rad, -1.4 + .01 * sin(a * 8.), cxz.y + sin(a) * rad);
    p.x += (r.z - .5) * .03;
    c = vec3(.6, .7, 1.) * (1. - age) * (1. - age) * .3;
    s = .02;
  }
}

// ---------------------------------------------------------------- a forever in a blink of an eye: the Helix, the eye in the sky
// a ring of glowing gas combed into fine radial filaments, gold at its inner edge burning out to red,
// a deep blue eye around a dark heart, a red haze beyond, and a field of blue-white stars
float gauss(float a, float b) { return sqrt(-2. * log(max(a, 1e-4))) * cos(TAU * b); }
vec2 helixShape(vec2 q) { q *= vec2(1.12, .92); return rot(-.35) * q; }
void fHelix(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float u = max(MT - 88.6, 0.);
  float w = r.w;
  s = .03;
  if (w < .012) {                    // the small hot star at its centre
    p = vec3(gauss(r.x + .001, r.y), gauss(r.y + .001, r.z), 0.) * .02;
    c = vec3(.75, .85, 1.) * .4;
  } else if (w < .55) {              // the ring: fine radial filaments
    float n = 1400.;
    float f = floor(r.x * n);
    float a = (f + .3 * (h1(f) - .5)) / n * TAU;
    float hf = h1(f * 1.37), hg = h1(f * 2.71);
    float r0 = 1.55 + .35 * hf + .12 * sin(a * 3. + 1.);          // where the filament starts (inner edge)
    float len = .35 + .75 * hg;
    float v = pow(r.y, .8);                                         // 0 at the bright inner head, 1 at the faint tail
    float R = (r0 + v * len) * 1.18;
    vec2 q2 = vec2(cos(a), sin(a)) * R;
    q2 += vec2(-sin(a), cos(a)) * gauss(r.z + .001, fract(r.z * 7.3)) * .012 * (1. + v * 2.);
    p = vec3(helixShape(q2), gauss(fract(r.z * 3.1) + .001, r.y) * .12);
    float lobe = .55 + .45 * sin(a * 2. + 2.2);                      // brighter on two opposite sides
    vec3 gold = vec3(1., .74, .34), red = vec3(.95, .3, .14);
    c = mix(gold * 1.15, mix(red, vec3(.55, .12, .1), smoothstep(.5, 1., v)), smoothstep(.0, .55, v));
    c *= .2 * lobe * (1. - .6 * v) * (.75 + .25 * tw(r.z, t, 1.3));
    c *= 1. + 1.6 * exp(-v * 14.) * step(.6, hg);                    // knots: bright heads on the inner edge
    s = .03;
  } else if (w < .66) {              // the eye: a blue glow inside the ring around a dark heart
    float R = (.35 + 1.4 * pow(r.y, .3)) * 1.18;
    float a = r.x * TAU;
    p = vec3(helixShape(vec2(cos(a), sin(a)) * R), gauss(r.z + .001, r.x) * .15);
    float e = R / (1.75 * 1.18);
    c = mix(vec3(.1, .2, .7), vec3(.25, .55, 1.), e) * .026 * smoothstep(.5, .95, e);
    s = .07;
  } else if (w < .8) {               // a red haze beyond the ring, faintly combed
    float a = r.x * TAU;
    float R = (2.6 + 2.2 * pow(r.y, .7)) * 1.18;
    p = vec3(helixShape(vec2(cos(a), sin(a)) * R * (1. + .05 * sin(a * 9. + r.y * 4.))), gauss(r.z + .001, r.y) * .3);
    c = vec3(.7, .16, .12) * .03 * (1. - r.y * .7);
    s = .08;
  } else {                           // stars: most small and white, a few large and blue
    vec2 xy = (vec2(r.x, r.y) - .5) * vec2(17., 10.);
    p = vec3(xy, -1.5 - fract(r.z * 5.3) * 3.);
    float big = step(.9975, fract(r.z * 13.7));
    c = mix(vec3(.85, .88, 1.), vec3(.45, .7, 1.), big) * (big > 0. ? 2.2 : .07 + .12 * tw(r.z, t, 1.5));
    s = big > 0. ? .085 : .016;
  }
  // a slow turn, the eye regarding us
  p.xy = rot(u * .012) * p.xy;
  p.xz = rot(.08 * sin(u * .3)) * p.xz;
  p.y += .2;
}

// ---------------------------------------------------------------- a pen made of olives
vec3 stemPath(float u) { vec2 g = glyphPt(u); return vec3(g.x, g.y - .05, -.15 * sin(u * PI * 3.)); }   // 花, in olive branches
void fOlive(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float head = smoothstep(93.3, 96.1, t);
  s = .02;
  if (r.w < .15) {
    float u = r.x;                   // waits along the whole branch, seen only once drawn
    p = stemPath(u) + (vec3(r.y, r.z, fract(r.y * 9.1)) - .5) * .05 * (1.3 - u);
    c = mix(vec3(.4, .5, .2), vec3(.75, .6, .3), r.y) * .22 * smoothstep(u, u + .02, head);
    c += GOLD * 1.5 * exp(-abs(head - u) * 60.) * step(u, head + .005) * step(.001, head);
    c *= mix(.3, 1., glyphDown(u) * 2. - 1.);
  } else if (r.w < .9) {
    float L = 46.; float k = floor(r.x * L); float u0 = (k + .5) / L;
    float side = mod(k, 2.) * 2. - 1.;
    float grow = smoothstep(0., .05, head - u0) * step(.75, glyphDown(u0));
    vec3 b = stemPath(u0);
    vec3 tn = normalize(stemPath(u0 + .01) - b);
    vec3 nr = vec3(-tn.y, tn.x, 0.);
    vec3 ld = normalize(tn * .55 + nr * side * .85 + vec3(0., 0., .25 * side));
    float len = (.34 + .22 * h1(k)) * (.35 + .65 * grow);
    float v = r.y;
    float w = .11 * sin(PI * pow(v, .8)) * len;
    vec3 lat = normalize(cross(ld, vec3(0., 0., 1.)));
    p = b + ld * v * len + lat * (r.z * 2. - 1.) * w + vec3(0., 0., .05 * sin(v * PI));
    float sil = tw(r.z + k, t, 1.3);
    c = mix(vec3(.42, .6, .2), vec3(.85, .92, .82), sil * .75) * (.12 + .1 * v) * step(.001, grow);
  } else {
    float k = floor(r.x * 9.); float u0 = .14 + k * .09 + .03 * h1(k);
    float ap = smoothstep(0., .1, head - u0) * step(.75, glyphDown(u0));
    vec3 b = stemPath(u0) + vec3(.06, -.16, .12);
    p = b + sph(r.y, r.z) * vec3(.08, .11, .08);
    c = mix(vec3(.3, .4, .12), vec3(.42, .2, .38), h1(k * 2.)) * .35 * ap;
  }
}

// ---------------------------------------------------------------- guitar and violin
// the dance quickens through the solo: speed .7 -> 2.2 (warped time, so trails stay continuous)
float soloX(float t) { return clamp((t - 97.6) / 56., 0., 1.); }
float soloSpeed(float t) { float x = soloX(t); return .7 + 1.5 * x * x; }
float soloPh(float t) { float x = soloX(t); return t * .7 + 28. * x * x * x + 1.5 * max(t - 153.6, 0.); }
float soloAmp(float t) { return mix(.35, 1., smoothstep(97.6, 110., t)) * (1. + .3 * smoothstep(118., 150., t)); }
float soloPeak(float t) { float y = clamp((t - 146.) / 7.4, 0., 1.); return y * y; }
vec3 ribbon(float tau, float w) {
  float x = soloX(tau);
  float f = soloPh(tau);
  float a = soloAmp(tau);
  float off = w * mix(2.1, 1.3, x);
  vec3 P = a * vec3(3.3 * sin(.37 * f + off) + 1.2 * sin(.83 * f + off * 1.7) + .7 * x * sin(1.9 * f + off * 2.3),
                    1.5 * sin(.53 * f + off * .6) + .55 * sin(1.21 * f + w) + .6 * x * sin(2.3 * f + off),
                    2.2 * cos(.29 * f + off * 1.3) + .6 * x * cos(1.7 * f + off * 1.9));
  // as it builds, the two lights stop answering from afar and start circling each other
  float wind = smoothstep(.35, .9, x);
  vec3 C = a * vec3(3.3 * sin(.37 * f + .6) + 1.2 * sin(.83 * f + 1.), 1.5 * sin(.53 * f + .4) + .55 * sin(1.21 * f + .5), 2.2 * cos(.29 * f + .8));
  float ang = f * 2.1 + w * PI;
  vec3 O = vec3(cos(ang), sin(ang * .8) * .5, sin(ang)) * mix(1.4, .8, x);
  P = mix(P, C + O, wind * .7);
  // the peak: both strings wind around each other and climb into the bridge
  float hx = smoothstep(145.8, 148.5, tau) * (1. - smoothstep(153.6, 155., tau));
  // the spiral keeps quickening and tightening until the bridge
  float y = clamp((tau - 146.) / 7.4, 0., 1.);
  float hp = f * 2. + 16. * y * y * y + w * PI;
  float R = 1.1 + .3 * sin(f * .7);
  vec3 Hx = vec3(cos(hp) * R, -3.2 + (tau - 146.) * .78 + 2. * y * y * y, sin(hp) * R);
  return mix(P, Hx, hx);
}
void fSolo(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float x = soloX(t);
  if (r.w < .88) {
    float w = step(.44, r.w);
    float sa = r.x;
    float tau = t - sa * (3.6 / soloSpeed(t) + 1.2);
    vec3 P = ribbon(tau, w);
    vec3 T = normalize(ribbon(tau + .04, w) - P + 1e-5);
    vec3 N = normalize(cross(T, vec3(0., 1., 0.)) + vec3(1e-4));
    vec3 B = cross(T, N);
    float twist = sa * 7. + soloPh(t) * 1.1 + w * PI;
    vec3 side = N * cos(twist) + B * sin(twist);
    float band = w < .5 ? uBand.y : uBand.z;
    float width = (.1 + .3 * band + .1 * x) * (1. - sa * .75);
    p = P + side * (r.y - .5) * 2. * width + sph(r.z, fract(r.z * 13.1)) * .015;
    vec3 cg = mix(vec3(1., .62, .3), vec3(1., .38, .45), sa);
    vec3 cv = mix(vec3(.82, .87, 1.), vec3(.55, .45, 1.), sa);
    c = (w < .5 ? cg : cv) * (.1 + .25 * band + .08 * x) * pow(1. - sa, 1.3) * (.4 + .6 * tw(r.z, t, 4.));
    c *= .55 + .9 * pow(abs(r.y - .5) * 2., 3.);
    c *= 1. + .5 * soloPeak(t);
    s = .018 + .02 * (1. - sa);
  } else {
    float w = step(.94, r.w);
    float age = fract(r.x + MT * (.25 + .35 * x)) * 4.;
    vec3 P = ribbon(t - age * .3, w);
    p = P + sph(r.y, r.z) * age * (.35 + .5 * r.y) * (1. + .6 * x) + vec3(0., -age * age * .05, 0.);
    c = (w < .5 ? GOLD : PEARL) * .45 * (.35 + .9 * x) * exp(-age * .9) * tw(r.y, t, 6.) * (1. + 1.5 * soloPeak(t));
    s = .02;
  }
}

// ---------------------------------------------------------------- the solo's journey: what the two lights fly over
void fDesert(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float d = mix(2.5, 80., pow(r.y, 2.2));
  float x = (r.x - .5) * 2. * (4. + d * .8);
  float z = 9. - d, zz = z - SCROLL;
  float arg = .42 * x + .2 * zz + 1.6 * sin(.11 * zz + x * .09);
  float crest = 1. - abs(sin(arg));
  float h = 2.1 * crest * crest + .6 * sin(x * .05 + zz * .03);
  p = vec3(x, -3.9 + h * (1. + d * .01), z);
  float lit = smoothstep(-.3, .3, cos(arg) * sign(sin(arg)));
  c = mix(vec3(.45, .2, .35) * .12, vec3(1., .64, .38), lit) * (.13 + .1 * tw(r.z, t, 1.2)) * (1. + 2.5 * pow(crest, 14.));
  c *= mix(1., .4, smoothstep(30., 80., d));
  s = .022 + d * .003;
  if (r.w > .88) {                          // sand blown off the crests
    float age = fract(r.z + MT * .4);
    p += vec3(age * 1.6, crest * age * .5, -age * .4) + flow(p * .5, MT) * .1 * age;
    c = vec3(1., .75, .5) * .25 * (1. - age) * pow(crest, 4.);
    s = .016;
  }
}
void fStarTrip(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float k = floor(r.x * 2400.);
  float u = r.w;                            // along each star's streak
  float z = 9. - mod(h1(k * 3.1) * 90. + SCROLL * 1.6, 90.);
  vec3 S = vec3((h1(k) - .5) * 44., (h1(k * 1.7) - .5) * 26., z);
  if (length(S.xy) < 2.5) S.xy *= 2.5 / max(length(S.xy), .01);   // keep the lights' stage clear
  float sp = soloSpeed(t);
  p = S - vec3(0., 0., u * (.2 + .9 * sp));
  float hk = h1(k * 5.3);
  c = (hk < .7 ? PEARL : (hk < .85 ? GOLD : LAV)) * 1.3 * pow(1. - u, 2.) * smoothstep(-80., -55., z) * (1. - smoothstep(4., 8.5, z));
  s = .026;
  if (r.z < .3) {                           // the galaxy's band, far behind
    float a = (r.y - .5) * 2.;
    p = vec3(a * 60., a * 14. + (fract(r.z * 17.) - .5) * 4. * (1. - abs(a) * .5), -70.);
    c = mix(LAV, PEARL, fract(r.z * 7.)) * .16 * tw(r.y, t, 1.1);
    s = .05;
  }
}
void fNebula(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  // billowing clouds on both sides of the path, sliding past
  float cl = floor(r.x * 30.);
  float side = mod(cl, 2.) * 2. - 1.;
  float zc = 9. - mod(h1(cl) * 64. + SCROLL * 1.1, 64.);
  vec3 C = vec3(side * (3.5 + h1(cl * 1.3) * 7.), (h1(cl * 2.1) - .5) * 9., zc);
  float rad = 2.2 + 3. * h1(cl * 3.7);
  vec3 o = sph(r.y, r.z) * pow(fract(r.x * 30.), .45) * rad;
  o += flow(o * .45 + cl, MT * .25) * rad * .35;
  p = C + o;
  float edge = smoothstep(.5, 1., length(o) / rad);
  vec3 col = mix(mix(TEAL, LAV, h1(cl * 5.)), ROSE, smoothstep(-.5, .8, o.y / rad + h1(cl * 7.) - .5));
  float fade = smoothstep(-55., -35., zc) * (1. - smoothstep(4., 8.5, p.z));
  c = col * (.16 + .4 * edge) * fade * (.7 + .3 * tw(r.w, t, 1.2));
  s = .075;
  if (r.w > .93) {                          // young stars inside them
    c = PEARL * .7 * tw(r.x, t, 3.) * fade;
    s = .02;
  }
}
// the open ocean all around them: a disc with no edge to see, melting into the dark (the camera circles it)
void fSeaRound(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float rho = r.w < .1 ? 3. * sqrt(r.y) : 3. + 67. * pow(r.y, 1.5);   // even near the middle, thinning far out
  float a = r.x * TAU;
  vec2 xz = vec2(cos(a), sin(a)) * rho;
  p = vec3(xz.x, -3.4 + waveH(xz, MT) * (1. + rho * .015), xz.y);
  float moon = exp(-pow(xz.x / (.4 + max(-xz.y, 0.) * .08), 2.)) * step(xz.y, 0.);   // the moon's road, toward -z
  float sp = tw(r.z, t, 2.5 + r.w * 7.);
  c = vec3(.24, .2, .44) * (.2 + .28 * sp) + vec3(.75, .8, 1.) * moon * (.04 + 1.1 * sp);
  c *= .8 * (1. - smoothstep(18., 70., rho));
  s = .026 + rho * .0032;
}
// env: 0 flower fields, 1 oceans, 2 deserts, 3 starfields, 4 nebulae, 5 the ocean they finally dance above
void fJourney(vec4 r, float t, int env, out vec3 p, out vec3 c, out float s) {
  if (r.w < .42) {
    vec4 e = vec4(r.xyz, r.w / .42);
    if (env == 0) { fFlowers(e, t, 60., 1e4, p, c, s); p.y -= 1.; }
    else if (env == 1) { fSea(e, t, .25, 0., p, c, s); p.y -= 1.3; }
    else if (env == 2) fDesert(e, t, p, c, s);
    else if (env == 3) fStarTrip(e, t, p, c, s);
    else if (env == 4) fNebula(e, t, p, c, s);
    else fSeaRound(e, t, p, c, s);
  } else {
    fSolo(vec4(r.xyz, (r.w - .42) / .58), t, p, c, s);
    c *= 1.45;
  }
}

// ---------------------------------------------------------------- an infinite spiral of lilies
// one lily in its own frame (z is where it faces); open: 0 a closed bud .. 1 tepals curled back
vec3 lilyPt(float a, float b, float d, float open, float ph, out vec3 col) {
  float k = floor(a * 6.);
  float az = (k + .5 * step(.94, d)) / 6. * TAU + ph;
  vec3 er = vec3(cos(az), sin(az), 0.), et = vec3(-sin(az), cos(az), 0.), ez = vec3(0., 0., 1.);
  if (d < .8) {                                   // six tepals, three outer and three inner
    float inner = mod(k, 2.);
    float v = b;
    float u = fract(a * 6.) * 2. - 1.;
    u = sign(u) * (1. - pow(1. - abs(u), 3.));    // more grains on the outline
    float W = mix(.19, .26, inner) * mix(.55, 1., open);
    float w = W * sin(PI * pow(v, .7)) * (1. - .3 * v);
    float th0 = mix(.1, .42, open), cu = mix(.35, 1.9, open) * mix(1., .9, inner);
    float th = th0 + cu * v;
    float L = mix(1., .93, inner);
    float R = L * (cos(th0) - cos(th)) / cu, Z = L * (sin(th) - sin(th0)) / cu;
    vec3 nrm = er * cos(th) - ez * sin(th);
    vec3 pt = er * R + ez * Z + et * u * w + nrm * (u * u * w * .45 - .02 * sin(v * 22. + k) * pow(abs(u), 3.));
    float spot = step(.9, h1(floor(v * 16.) + floor(u * 4.) * 17. + k * 31.)) * bump(v, .12, .2, .5, .62) * step(abs(u), .7);
    col = mix(GOLD * 1.2, mix(ROSE, PEARL, smoothstep(.2, .85, abs(u))), smoothstep(.05, .32, v));
    col = mix(col, ROSE * 1.6, spot);
    col *= .5 + .9 * pow(abs(u), 3.);
    return pt;
  }
  if (d < .94) {                                  // six stamens with their anthers
    float v = b;
    if (fract(a * 6.) < .6) {
      col = PEARL * .45;
      return (er * v * mix(.06, .38, open) + ez * v) * .78;
    }
    vec3 tip = (er * mix(.06, .38, open) + ez) * .78;
    col = vec3(1., .5, .22) * 1.8;
    return tip + et * (v - .5) * .09 + sph(fract(a * 37.), fract(d * 53.)) * .012;
  }
  float v = b;                                    // the pistil and its stigma
  if (v < .8) { col = PEARL * .4; return ez * v * 1.05 + er * .04 * v * v; }
  col = ROSE * 1.2;
  return ez * .86 + er * .045 + sph(fract(a * 19.), fract(d * 71.)) * .035;
}
void fFleur(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float NL = 44., ARMS = 2.;
  float li = floor(r.x * NL);
  float arm = mod(li, ARMS), j = floor(li / ARMS);
  float tt = MT - 153.;
  float rho = fract(j / (NL / ARMS) + tt * .04);  // born at the heart, carried outwards forever
  float lr = rho * 4.2;
  float rad = .12 * exp(lr);
  float ang = arm / ARMS * TAU + lr / .3 - tt * .05;
  vec2 dir = vec2(cos(ang), sin(ang));
  vec3 ez = normalize(vec3(dir * .45, 1.));
  vec3 e2 = vec3(-dir.y, dir.x, 0.), e1 = cross(e2, ez);
  float open = smoothstep(.06, .4, rho);
  vec3 col;
  vec3 l = lilyPt(fract(r.x * NL), r.y, r.w, open, h1(li) * TAU + tt * .08, col);
  float sz = rad * .36;
  p = vec3(dir * rad, -rad * .22 + .05 * sin(t * .7 + li)) + (e1 * l.x + e2 * l.y + ez * l.z) * sz;
  float life = smoothstep(0., .12, rho) * (1. - smoothstep(.72, 1., rho));
  c = col * (.1 + .2 * tw(r.z, t, 2.)) * life;
  s = .013 + rad * .004;
}

// ---------------------------------------------------------------- sunsets and sunrises, forever
float cycPh(float t) { float x = clamp((t - 165.) / 11.4, 0., 1.); return (t - 163.5) + 8. * (11.4 * x * x * x / 3. + max(t - 176.4, 0.)); }
void fCycle(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float ph = cycPh(MT);
  float boost = pow(clamp((MT - 165.) / 11.4, 0., 1.), 1.5);   // keeps building until the chorus
  float u = fract(ph * .2 + .5);
  float day = smoothstep(0., .12, u) * (1. - smoothstep(.6, .76, u));
  if (r.w < .3) { fSea(r, t, .3 + .5 * day, 0., p, c, s); c *= mix(.45, 1., day) * (1. + boost); return; }
  vec3 O = vec3(0., -1.4, -42.);
  float ri = floor(r.y * 28.);
  float R = 4.5 + ri * 1.3;
  float dirn = mod(ri, 2.) * 2. - 1.;
  float head = (ph + .5) * .55 * dirn + h1(ri) * TAU;
  float trail = r.x;
  float a = head - trail * mix(2.8, 5.8, boost) * dirn;
  p = O + vec3(cos(a) * R, sin(a) * R, (r.z - .5) * .4);
  bool sun = mod(ri, 2.) < 1.;
  c = (sun ? vec3(1., .6, .35) : vec3(.7, .8, 1.)) * pow(1. - trail, mix(2.2, 1.4, boost)) * .32 * (1. + 2.2 * boost);
  if (p.y < -1.4) { p.y = -1.4 - (-1.4 - p.y) * .5; c *= .3; }
  s = .03 + R * .0035;
}

// ---------------------------------------------------------------- a billion destinies: strings, branes and folded dimensions
vec4 tessV(float i) {                       // a corner of the hypercube, from the bits of i
  return vec4(mod(i, 2.), mod(floor(i * .5), 2.), mod(floor(i * .25), 2.), mod(floor(i * .125), 2.)) * 2. - 1.;
}
// turn through the fourth dimension (xw, zw, yw), then cast its shadow into ours
vec3 tessR(vec4 v, vec3 an, float sc, out float w) {
  v.xw = rot(an.x) * v.xw;
  v.zw = rot(an.y) * v.zw;
  v.yw = rot(an.z) * v.yw;
  w = v.w;
  vec3 q = v.xyz * sc / (3.2 - v.w);
  q.xz = rot(.5 + (MT - 184.) * .08) * q.xz;
  q.yz = rot(.38) * q.yz;
  return q + vec3(0., .3, 0.);
}
vec3 tessAng(float tt) { return vec3(tt * .5, tt * .31 + .6, tt * .12); }
vec3 tessPair(float k, float t) {           // you and i ride two opposite corners
  float w; MT = t; return tessR(tessV(k < .5 ? 0. : 15.), tessAng(t - 184.), 4.1, w);
}
// one edge of the hypercube as a vibrating string: a standing wave in the two dimensions it doesn't run along
vec4 tessString(float e, float u, float ph, float amp) {
  float d = floor(e / 8.), j = mod(e, 8.);
  float pd = pow(2., d);
  float i0 = floor(j / pd) * pd * 2. + mod(j, pd);
  vec4 P = mix(tessV(i0), tessV(i0 + pd), u);
  vec4 ax1 = vec4(equal(vec4(0., 1., 2., 3.), vec4(mod(d + 1., 4.))));
  vec4 ax2 = vec4(equal(vec4(0., 1., 2., 3.), vec4(mod(d + 2., 4.))));
  float n = 1. + floor(h1(e * 3.7) * 4.);   // its harmonic
  float om = 5. + n * 2.3;
  float wave = sin(n * PI * u) * amp;
  return P + (ax1 * cos(om * ph + e) + ax2 * sin(om * ph + e) * .6) * wave;
}
// a Calabi–Yau cross-section (the quintic, z1^5 + z2^5 = 1): where the extra dimensions are folded away
vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
vec2 cpow(vec2 z, float k) { float r = length(z); float a = atan(z.y, z.x + 1e-7); return pow(r + 1e-7, k) * vec2(cos(k * a), sin(k * a)); }
vec3 calabi(float k1, float k2, float a, float b, float al) {
  float ch = (exp(b) + exp(-b)) * .5, sh = (exp(b) - exp(-b)) * .5;
  vec2 cs = vec2(cos(a) * ch, -sin(a) * sh);
  vec2 sn = vec2(sin(a) * ch, cos(a) * sh);
  vec2 z1 = cmul(vec2(cos(TAU * k1 / 5.), sin(TAU * k1 / 5.)), cpow(cs, .4));
  vec2 z2 = cmul(vec2(cos(TAU * k2 / 5.), sin(TAU * k2 / 5.)), cpow(sn, .4));
  return vec3(z1.x, z2.x, cos(al) * z1.y + sin(al) * z2.y);
}
void fDestiny(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float tt = MT - 184.;
  float sel = r.w;
  float w;
  s = .016;
  if (sel < .48) {                          // the hypercube: strings, and their superposed ghosts
    float ghost = step(.36, sel);
    float e = floor(r.x * 32.);
    float grow = smoothstep(0., 1., (t - 184.3 - h1(e) * 1.1) / .8);
    float env = step(.5, fract(sel * 37.));  // half the grains smear over the wave's period: its envelope
    float ph = MT + env * fract(r.z * 7.3) * 1.3;
    vec4 P = tessString(e, r.y * grow, ph, .085 * (1. + .6 * uLvl));
    vec3 an = tessAng(tt);
    if (ghost > 0.) {                       // the other outcomes, a little out of phase
      float g = floor(fract(sel * 11.) * 4.);
      float dl = (g < 2. ? -1. : 1.) * (.28 + .28 * mod(g, 2.));
      an += vec3(dl, -dl * .7, dl * .5);
    }
    p = tessR(P, an, 4.1, w) + sph(r.z, fract(sel * 13.)) * .008;
    c = mix(GOLD, PEARL, .5 + .5 * w) * (env > 0. ? .1 : .2) * mix(1., .5, ghost) * step(.001, grow);
    c *= .75 + .5 * tw(r.y + e, t, 2.);
  } else if (sel < .56) {                   // corners as probability clouds
    float i = floor(r.x * 16.);
    vec3 dir = sph(r.y, r.z);
    vec3 lobe = normalize(vec3(h1(i), h1(i + 3.), h1(i + 7.)) - .5 + 1e-3);
    float ph = MT * (1.3 + h1(i * 5.));
    lobe.xy = rot(ph) * lobe.xy;
    float rad = .26 * pow(abs(dot(dir, lobe)), 1.5) * sqrt(fract(sel * 29.));
    p = tessR(tessV(i), tessAng(tt), 4.1, w) + dir * rad;
    c = mix(GOLD, PEARL, .5 + .5 * w) * .3 * (.5 + .5 * tw(r.x, t, 5.)) * smoothstep(184. + h1(i) * .6, 184.6 + h1(i) * .6, t);
    s = .02;
  } else if (sel < .64) {                   // a higher hypercube around it all, turning the other way
    float e = floor(r.x * 32.);
    vec4 P = tessString(e, r.y, MT * .6, .05);
    p = tessR(P, vec3(-tt * .23 + 1.3, .4 - tt * .11, tt * .19), 8.4, w) + sph(r.z, fract(sel * 13.)) * .01;
    c = mix(LAV, TEAL, .5 + .5 * w) * .17 * smoothstep(184.8, 186.2, t) * (.6 + .4 * tw(r.y + e, t, 1.5));
    s = .02;
  } else if (sel < .93) {                   // the folded dimensions at the heart
    float k1 = floor(r.x * 5.), k2 = floor(fract(r.x * 5.) * 5.);
    float a = r.y * PI * .5;
    float b = (r.z * 2. - 1.) * 1.15;
    vec3 q = calabi(k1, k2, a, b, .7 + tt * .45);
    q.xy = rot(tt * .3) * q.xy;
    q.yz = rot(.5 + tt * .17) * q.yz;
    float bloom = smoothstep(184.5, 186., t);
    p = q * .95 * bloom + vec3(0., .3, 0.);
    float edge = smoothstep(.6, 1., abs(r.z * 2. - 1.));
    c = mix(spec(k1 * .2 + k2 * .07 + tt * .05) * .8 + .2, PEARL, .3) * (.17 + .25 * edge) * (.6 + .4 * tw(r.y + k1, t, 2.5)) * bloom;
    s = .014;
  } else {                                  // a faint sky of other stars
    p = vec3((r.y - .5) * 26., (r.z - .5) * 14., -6. - fract(sel * 7.) * 10.);
    c = PEARL * .1 * tw(r.z, t, 1.3);
  }
}

// ---------------------------------------------------------------- one: one plumeria
void fPlumeria(vec4 r, float t, out vec3 p, out vec3 c, out float s) {
  float late = smoothstep(224.5, 238., t);
  float open = smoothstep(199.8, 205.4, t);              // the twisted bud unfurls
  open *= 1. - .6 * smoothstep(238.5, 246., t);           // and folds again at the end
  float spin = (MT - 199.) * .025;
  if (r.w > .94) {             // the golden throat
    float a = r.x * TAU;
    float d = r.y;
    float rad = mix(.12, .03, d) * (.4 + .6 * open) + .01;
    p = vec3(cos(a) * rad, sin(a) * rad, .05 - d * .35);
    c = mix(vec3(1., .6, .16), vec3(1., .76, .32), d) * .12 * (.6 + .4 * tw(r.z, t, 2.));
    s = .02;
  } else {
    float k = floor(r.x * 5.);
    float u = r.y;                                        // root to tip
    float v = r.z * 2. - 1.;                              // edge to edge
    float vein = step(r.w, .2);
    if (vein > 0.) v = clamp((floor(fract(r.w * 31.) * 3.) - 1.) * .42 * pow(u, .8), -1., 1.);
    float len = 1.95 * (1. + .02 * sin(MT * .5));
    float wid = .74 * pow(max(sin(PI * pow(u, 1.7)), 0.), .5) * (v > 0. ? 1.15 : .85);
    // pinwheel: a bud is a tight spiral; open, each petal sweeps a little off its radial line
    float ang = k / 5. * TAU + spin + u * mix(2.4, .62, open);
    float tilt = mix(.16, 1.38, open);
    vec3 rad = vec3(cos(ang), sin(ang), 0.), tng = vec3(-sin(ang), cos(ang), 0.), ax = vec3(0., 0., 1.);
    vec3 along = cos(tilt) * ax + sin(tilt) * rad;
    vec3 nrm = -sin(tilt) * ax + cos(tilt) * rad;         // the petal's back
    float twist = (.24 + .12 * (1. - open)) * u;          // leading edge lifts, trailing edge tucks under the next petal
    p = along * (.06 + u * len) + tng * v * wid - nrm * v * twist * wid * 1.2;
    p += -nrm * v * v * wid * .16 + nrm * u * u * .34 * open;   // waxy cup, soft recurve at the tip
    if (vein > 0.) {           // written with His words: lines of light drawn from the heart outward
      float wr = smoothstep(204.4 + k * .25, 208.8, t);
      float lit = smoothstep(u - .05, u, wr * 1.05);
      c = GOLD * lit * (.07 + .13 * tw(r.z, t, 2.5)) + vec3(1., .92, .85) * .012;
    } else {
      vec3 pc = mix(vec3(1., .64, .2), vec3(1., .95, .88), smoothstep(.04, .42, u));
      pc = mix(pc, vec3(1., .74, .82), smoothstep(.62, 1., u) * pow(abs(v), 3.) * .45);
      float edge = pow(abs(v), 8.) + smoothstep(.94, 1., u) * .7;
      c = pc * (.03 + .05 * tw(r.z, t, 1.6)) * (.5 + 2. * edge) * mix(.45, 1., smoothstep(0., .3, u));
    }
    s = .018;
  }
  p.yz = rot(.42) * p.yz;
  c *= 1. - .25 * late;
}
// the ending: light leaves the flower and traces her, the girl who wears it in her hair
// (points from js/girl.js: the lines grow out from the hairpin along her hair; then her face fills with glow)
uniform sampler2D uGirl1, uGirl2;           // x, y (world, the flower at the origin), order, kind | colour, strength
uniform float uGirlN, uGirlH;
uniform vec4 uGirlHead;                     // head centre, neck (world)
// she is alive: she breathes, her head drifts a little with it (the hairpin too), and her hair sways
float breath(float t) { return sin(t * TAU / 4.8); }
vec3 headMove(vec3 p, float t) {
  vec2 nk = uGirlHead.zw;
  float a = .016 * sin(t * .55) + .006 * sin(t * 1.37 + 1.);
  p.xy = nk + rot(a) * (p.xy - nk);
  p.y += .06 * breath(t);
  return p;
}
vec3 aliveGirl(vec3 p, float kind, float t) {
  if (kind > 3.5) {                         // shoulders rise and fall with each breath
    p.y += .1 * breath(t);
    return p;
  }
  p = headMove(p, t);
  if (kind < .5) {                          // her hair sways, more toward the ends, in slow waves
    float d = length(p.xy - uGirlHead.xy);
    float amp = smoothstep(2.5, 9., d) * .3;
    float w = sin(t * 1.05 - d * .5 + p.y * .15) + .4 * sin(t * 1.7 - d * .9);
    p.x += w * amp;
    p.y += .25 * amp * sin(t * .9 - d * .4);
  }
  return p;
}
void fOne(vec4 r, vec4 q, float t, out vec3 p, out vec3 c, out float s) {
  float alive = smoothstep(209.5, 213., t);
  if (r.w < .62) {
    vec3 P0, C0; float S0;
    fPlumeria(vec4(r.xyz, fract(r.w * 9.7)), t, P0, C0, S0);   // its place in the flower until it is called out
    P0 = mix(P0, headMove(P0, t), alive);
    float idx = floor(fract(r.x * 7.31 + r.y * .17) * uGirlN);
    vec2 uv = vec2((mod(idx, 256.) + .5) / 256., (floor(idx / 256.) + .5) / uGirlH);
    vec4 g1 = texture2D(uGirl1, uv), g2 = texture2D(uGirl2, uv);
    float line = abs(g1.w - 1.) > .5 ? 1. : 0.;   // kind 1 is the glow of her face
    float Tr = line > .5 ? 209.8 + g1.z * 16. : 223. + g1.z * 8.;
    float k = smoothstep(Tr - 1.2, Tr, t);
    vec3 T = aliveGirl(vec3(g1.xy, -.35 - .03 * g1.w), g1.w, t) + sph(r.y, r.z) * .01;
    float e = k * k * (3. - 2. * k);
    p = mix(P0, T, e) + vec3(0., 0., 1.) * sin(PI * e) * length(T - P0) * .12;
    vec3 Cg = g2.rgb * mix(.25, .6, g2.w) * (line > .5 ? 1.2 : .3) * (.75 + .5 * tw(r.z, t, 1.5));
    c = mix(C0, Cg, smoothstep(.55, 1., k)) + GOLD * sin(PI * k) * .2;   // a spark as it flies
    s = mix(S0, line > .5 ? .024 : .03, k);
  } else {
    fPlumeria(vec4(r.xyz, (r.w - .62) / .38), t, p, c, s);
    p = mix(p, headMove(p, t), alive);           // the hairpin moves with her
  }
}

// ----------------------------------------------------------------
void form(int id, vec4 r, vec4 q, float t, out vec3 p, out vec3 c, out float s) {
  MT = t;
  SCROLL = 0.; OPEN = 0.;
  if (id >= 20) { SCROLL = soloPh(t) * 2.2; OPEN = 1.; }
  if (id == 11) OPEN = 1.;
  if (id == 0) fSeed(r, q, t, p, c, s);
  else if (id == 1) fSea(r, t, 0., 0., p, c, s);
  else if (id == 2) fDawn(r, t, p, c, s);
  else if (id == 3) fWorld(r, q, t, p, c, s);
  else if (id == 4) fFlowers(r, t, 65.5, 1e4, p, c, s);
  else if (id == 5) fGalaxy(r, t, p, c, s);
  else if (id == 6) fInk(r, t, 77.4, 80.3, 0., p, c, s);
  else if (id == 7) fBye(r, t, p, c, s);
  else if (id == 8) fRain(r, t, 1., p, c, s);
  else if (id == 9) fHelix(r, t, p, c, s);
  else if (id == 10) fOlive(r, t, p, c, s);
  else if (id == 11) fJourney(r, t, 5, p, c, s);
  else if (id == 20) fJourney(r, t, 0, p, c, s);
  else if (id == 21) fJourney(r, t, 1, p, c, s);
  else if (id == 22) fJourney(r, t, 2, p, c, s);
  else if (id == 23) fJourney(r, t, 3, p, c, s);
  else if (id == 24) fJourney(r, t, 4, p, c, s);
  else if (id == 12) fFleur(r, t, p, c, s);
  else if (id == 13) fCycle(r, t, p, c, s);
  else if (id == 15) fFlowers(r, t, 175.2, 177.6, p, c, s);
  else if (id == 16) fDestiny(r, t, p, c, s);
  else if (id == 17) fInk(r, t, 188.4, 191., 1., p, c, s);
  else if (id == 18) fRain(r, t, -1., p, c, s);
  else fOne(r, q, t, p, c, s);
}

// the two small lights: you and me
void pair(int id, float t0, out vec3 a, out vec3 b) {
  float t = t0;
  float mt = t0;
  a = vec3(-.2, 0., 0.); b = vec3(.2, 0., 0.);
  if (id == 0) { float rr = .05 + uSeed.x * .05; float an = mt * .6; a = vec3(cos(an), sin(an), .2) * rr; b = vec3(-a.x, -a.y, a.z); }
  else if (id == 1 || id == 2 || id == 13) { a = vec3(-.2, -1.24, 3.); b = vec3(.2, -1.24, 3.); }
  else if (id == 3) { a = EARTH; b = moonPos(t); }   // you and me are the earth and the moon here
  else if (id == 4 || id == 15) { float y = -.5 + .08 * sin(mt * 1.3); a = vec3(-.22, y, 4.); b = vec3(.22, y + .05 * sin(mt * 1.1), 4.); if (id == 15) { float u = smoothstep(178., 184., t); a.y += u * 2.; b.y += u * 2.; } }
  else if (id == 5) { float u = fract(mt * .35); a = axonPt(0., u); b = axonPt(1., fract(u + .5)); }
  else if (id == 6 || id == 17) { float t0 = id == 6 ? 77.4 : 188.4, t1 = id == 6 ? 80.3 : 191.; float m = id == 6 ? 0. : 1.; float hd = smoothstep(t0, t1, t); a = inkPath(hd, m) + vec3(0., 0., .2); b = inkPath(max(hd - .025, 0.), m) + vec3(0., .12, .2); }
  else if (id == 7) { float u = t - 81.; a = vec3(-.3 - u * .45, .3 + u * .05, -u * .3); b = vec3(.3 + u * .45, .3, -u * .3); }
  else if (id == 8) { float u = smoothstep(85.5, 89., t); a = vec3(-2.6 + u * .8, -1.1, 1.); b = vec3(2.6 - u * .8, -1.1, 1.); }
  else if (id == 9) { float an = mt * 2.2; a = vec3(cos(an), sin(an), 0.) * .05 + vec3(0., .2, .05); b = vec3(-cos(an), -sin(an), 0.) * .05 + vec3(0., .2, .05); }
  else if (id == 10) { float hd = smoothstep(93.3, 96.1, t); a = stemPath(hd) + vec3(0., 0., .15); b = stemPath(max(hd - .06, 0.)) + vec3(0., .15, .15); }
  else if (id == 11 || id >= 20) { a = ribbon(t, 0.); b = ribbon(t, 1.); }
  else if (id == 12) { float an = mt * .5; a = vec3(cos(an), sin(an), 0.) * .22; b = -a; }
  else if (id == 16) { a = tessPair(0., t); b = tessPair(1., t); float m = smoothstep(187.4, 188., t); a = mix(a, vec3(0., .3, 0.), m); b = mix(b, vec3(0., .3, 0.), m); }
  else if (id == 18) { float u = smoothstep(192., 199., t); a = vec3(-2.2 + u * 1.9, -1.1 + u * 1.2, 1.); b = vec3(2.2 - u * 1.9, -1.1 + u * 1.2, 1.); }
  else { float m = 1. - smoothstep(199.6, 202.4, t); float an = mt * 1.6; a = vec3(cos(an), sin(an), 0.) * .7 * m + vec3(0., 0., .12); b = vec3(-cos(an), -sin(an), 0.) * .7 * m + vec3(0., 0., .12); }
}

void main() {
  vec4 r = aR, q = aQ;
  vec3 p, c; float s;
  // stagger: each grain leaves at its own moment, so a change flows instead of cutting
  float k = clamp(uMix * 1.6 - r.w * .6, 0., 1.);
  k = k * k * (3. - 2. * k);
  int grp = int(q.y + .5);
  if (grp == 3) {
    // dust that hangs in the air of every scene
    float wt = uTB;
    p = (fract(r.xyz + vec3(wt * .004, wt * .006, wt * .003)) - .5) * vec3(28., 15., 26.) + vec3(0., 0., -2.);
    c = mix(LAV, GOLD, r.w) * .07 * tw(r.w, uTB, 1.2) * uDust;
    s = .022;
  } else if (grp == 0) {
    vec3 pA, cA; float sA;
    form(uB, r, q, uTB, p, c, s);
    if (uA != uB && k < 1.) {
      form(uA, r, q, uTA, pA, cA, sA);
      vec3 sw = flow(mix(pA, p, .5) * .4 + r.xyz, uTB * .5) * sin(PI * k) * uSwirl;
      p = mix(pA, p, k) + sw;
      c = mix(cA, c, smoothstep(0., .55, k));   // colour settles early, so a change never flares
      s = mix(sA, s, k);
    }
  } else {
    vec3 a0, b0, a1, b1;
    pair(uB, uTB, a1, b1);
    float kk = uMix * uMix * (3. - 2. * uMix);
    if (uA != uB && uMix < 1.) { pair(uA, uTA, a0, b0); a1 = mix(a0, a1, kk); b1 = mix(b0, b1, kk); }
    vec3 ctr = grp == 1 ? a1 : b1;
    float halo = step(.7, r.w);
    float rr = mix(.075 * pow(r.z, 2.), .32 * pow(r.z, 1.5), halo);
    p = ctr + sph(r.x, r.y) * rr;
    vec3 tint = grp == 1 ? vec3(1., .74, .52) : vec3(.74, .84, 1.);
    float shown = smoothstep(97.4, 99.4, uTB);   // the two lights first appear with the instrumental break
    c = tint * mix(.09, .012, halo) * (1. + .4 * uLvl) * uHold * shown;
    s = mix(.016, .03, halo);
  }

  vec4 mv = modelViewMatrix * vec4(p, 1.);
  float px = s * uSizeK * uPx / max(-mv.z, .05);
  float gain = uGain * (.78 + .5 * uLvl);
  if (px < 1.3) { gain *= (px / 1.3) * (px / 1.3); px = 1.3; }
  gl_PointSize = min(px, 80.);
  vCol = c * gain;
  if (mv.z > -.05) vCol = vec3(0.);
  gl_Position = projectionMatrix * mv;
}
`;

  const FRAG = /* glsl */`
precision highp float;
varying vec3 vCol;
void main() {
  vec2 d = gl_PointCoord * 2. - 1.;
  float r2 = dot(d, d);
  if (r2 > 1.) discard;
  float f = exp(-r2 * 3.5) * (1. - r2);
  gl_FragColor = vec4(vCol * f, 1.);
}
`;

  const SKY_V = /* glsl */`
varying vec3 vDir;
void main() { vDir = position; vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.); gl_Position = p.xyww; }
`;
  const SKY_F = /* glsl */`
precision highp float;
varying vec3 vDir;
uniform vec3 uZen, uHor, uSunDir, uSunCol;
uniform float uSunI, uStars, uT, uStorm, uOn, uAur, uHigh, uGain;
float hash(vec3 p) { p = fract(p * vec3(.1031, .1030, .0973)); p += dot(p, p.yxz + 33.33); return fract((p.x + p.y) * p.z); }
void main() {
  vec3 d = normalize(vDir);
  float el = d.y;
  float hz = exp(-abs(el) * mix(5.5, 9., step(el, 0.)));
  vec3 col = mix(uZen, uHor, hz);
  col *= mix(1., .55, smoothstep(0., -.3, el));
  col *= uGain;
  float sd = max(dot(d, normalize(uSunDir)), 0.);
  col += uSunCol * uSunI * (pow(sd, 3000.) * 2.2 + pow(sd, 180.) * .12 + pow(sd, 10.) * .05) * smoothstep(-.03, .01, el);
  // stars
  vec3 sp = d * 220.;
  vec3 cell = floor(sp);
  float h = hash(cell);
  vec3 off = vec3(hash(cell + 1.7), hash(cell + 3.1), hash(cell + 5.3));
  float dd = length(fract(sp) - off);
  float tw = .6 + .4 * sin(uT * (1. + h * 3.) + h * 40.);
  col += vec3(.85, .88, 1.) * step(.975, h) * smoothstep(.12, 0., dd) * tw * uStars * smoothstep(-.02, .15, el) * 1.4;
  // storm: low clouds that flash with the drums
  col += vec3(.5, .55, .9) * uStorm * uOn * uOn * .25 * exp(-max(el, 0.) * 3.);
  // a quiet aurora above the solo
  float aur = uAur * smoothstep(.05, .35, el) * (1. - smoothstep(.45, .9, el));
  float wave = .5 + .5 * sin(d.x * 6. + uT * .3 + sin(d.z * 4. - uT * .2) * 2.);
  col += mix(vec3(.15, .5, .45), vec3(.45, .25, .7), wave) * aur * (.04 + .08 * uHigh) * wave;
  gl_FragColor = vec4(col, 1.);
}
`;

  const GRADE = {
    uniforms: { tDiffuse: { value: null }, uT: { value: 0 }, uRes: { value: new THREE.Vector2(1, 1) }, uExp: { value: 1 } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }',
    fragmentShader: /* glsl */`
precision highp float;
uniform sampler2D tDiffuse; uniform float uT, uExp; uniform vec2 uRes;
varying vec2 vUv;
vec3 aces(vec3 x) { return clamp((x * (2.51 * x + .03)) / (x * (2.43 * x + .59) + .14), 0., 1.); }
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main() {
  vec3 c = texture2D(tDiffuse, vUv).rgb * uExp;
  c = aces(c * .95);
  c = mix(vec3(dot(c, vec3(.2126, .7152, .0722))), c, .94);
  c += vec3(.012, .009, .022) * (1. - c);
  vec2 v = vUv - .5; v.x *= uRes.x / uRes.y;
  c *= 1. - .42 * smoothstep(.35, 1.05, length(v));
  c = pow(c, vec3(1. / 2.2));
  c += (hash(vUv * uRes + fract(uT * 7.)) - .5) * .022;
  gl_FragColor = vec4(c, 1.);
}
`
  };

  const G = H.G = {};

  G.init = function (canvas) {
    const coarse = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 600;
    G.N = coarse ? 60000 : 120000;
    const renderer = G.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.setClearColor(0x05050c, 1);
    G.dprMax = coarse ? 1.5 : 1.75;
    G.scale = 1;

    const scene = G.scene = new THREE.Scene();
    const camera = G.camera = new THREE.PerspectiveCamera(45, 1, 0.05, 900);

    // sky
    G.skyU = {
      uZen: { value: new THREE.Vector3() }, uHor: { value: new THREE.Vector3() }, uSunDir: { value: new THREE.Vector3(0, 0.05, -1) },
      uSunCol: { value: new THREE.Vector3(1, 0.7, 0.5) }, uSunI: { value: 0 }, uStars: { value: 0.7 }, uT: { value: 0 },
      uStorm: { value: 0 }, uOn: { value: 0 }, uAur: { value: 0 }, uHigh: { value: 0 }, uGain: { value: 0.42 }
    };
    const sky = G.sky = new THREE.Mesh(new THREE.SphereGeometry(400, 48, 24),
      new THREE.ShaderMaterial({ uniforms: G.skyU, vertexShader: SKY_V, fragmentShader: SKY_F, side: THREE.BackSide, depthWrite: false, depthTest: false }));
    sky.renderOrder = -1;
    sky.frustumCulled = false;
    scene.add(sky);

    // the light
    const N = G.N;
    const aR = new Float32Array(N * 4), aQ = new Float32Array(N * 4);
    let seed = 1234567;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    const nYou = Math.round(N * 0.012), nDust = Math.round(N * 0.02);
    for (let i = 0; i < N; i++) {
      for (let j = 0; j < 4; j++) aR[i * 4 + j] = rnd();
      let g = 0;
      if (i < nYou) g = 1; else if (i < nYou * 2) g = 2; else if (i < nYou * 2 + nDust) g = 3;
      aQ[i * 4] = i / N;
      aQ[i * 4 + 1] = g;
      aQ[i * 4 + 2] = rnd();
      aQ[i * 4 + 3] = rnd();
    }
    // spread the index fraction of the light grains over 0..1 (forms that use it need it dense)
    const nLight = N - nYou * 2 - nDust;
    for (let i = nYou * 2 + nDust, j = 0; i < N; i++, j++) aQ[i * 4] = j / nLight;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    geo.setAttribute('aR', new THREE.BufferAttribute(aR, 4));
    geo.setAttribute('aQ', new THREE.BufferAttribute(aQ, 4));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);

    G.U = {
      uN: { value: nLight }, uTA: { value: 0 }, uTB: { value: 0 }, uMix: { value: 1 }, uClock: { value: 0 },
      uLvl: { value: 0.5 }, uOn: { value: 0 }, uBeat: { value: 0 }, uPx: { value: 800 },
      uGain: { value: Math.pow(120000 / N, 0.75) }, uSizeK: { value: Math.pow(120000 / N, 0.25) },
      uDust: { value: 1 },
      uSwirl: { value: 0.9 }, uHold: { value: 1 },
      uA: { value: 0 }, uB: { value: 0 }, uBand: { value: new THREE.Vector4() }, uSeed: { value: new THREE.Vector4(1.15, 0, 1, 0) },
      uGlyph: { value: new Float32Array(H.GN * 4) },
      uGirl1: { value: null }, uGirl2: { value: null }, uGirlN: { value: 1 }, uGirlH: { value: 1 },
      uGirlHead: { value: new THREE.Vector4() },
      uMet: { value: null }, uMoonHit: { value: new Float32Array(24 * 4) }, uEarthHit: { value: new Float32Array(16 * 4) }
    };
    buildMeteors(G.U);
    buildGirl(G.U);
    const mat = new THREE.ShaderMaterial({
      uniforms: G.U, vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
    });
    const pts = G.points = new THREE.Points(geo, mat);
    pts.frustumCulled = false;
    scene.add(pts);

    // lens
    const composer = G.composer = new THREE.EffectComposer(renderer);
    composer.addPass(new THREE.RenderPass(scene, camera));
    G.bloom = new THREE.UnrealBloomPass(new THREE.Vector2(256, 256), 0.8, 0.7, 0.5);
    composer.addPass(G.bloom);
    G.grade = new THREE.ShaderPass(GRADE);
    composer.addPass(G.grade);

    G.resize();
    addEventListener('resize', G.resize);
  };

  G.resize = function () {
    const w = innerWidth, h = innerHeight;
    const dpr = Math.min(devicePixelRatio || 1, G.dprMax) * G.scale;
    G.renderer.setPixelRatio(dpr);
    G.renderer.setSize(w, h, false);
    G.composer.setPixelRatio(dpr);
    G.composer.setSize(w, h);
    G.camera.aspect = w / h;
    G.camera.updateProjectionMatrix();
    G.grade.uniforms.uRes.value.set(w * dpr, h * dpr);
    G.H = h * dpr;
  };

  // st: { A, B, k, tA, tB, t, clock, cam, sky, f (features), beat, seed, hold }
  // the words the light writes (text.js "written"), traced once into pen strokes
  // ---- the storm of meteors, flown once with real gravity (same constants as the shader's earth and moon)
  function buildMeteors(U) {
    const E = [0, 0.3, 0], RE = 1.25, RM = 0.36, RO = 3, W0 = 0.42, GM = 16;
    const norm = v => { const l = Math.hypot(...v) || 1; return v.map(x => x / l); };
    const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    const add = (a, b, k = 1) => a.map((x, i) => x + b[i] * k);
    const N = norm([-0.22, 1, 0.1]), X = norm(cross(N, [0, 0, 1])), Y = cross(N, X);
    // the same orbit as the shader: steady, then giving way from TFALL
    const TFALL = 59.4, TIMPACT = 63.3;
    const decay = t => Math.pow(Math.min(1, Math.max(0, (t - TFALL) / (TIMPACT - TFALL))), 2.4);
    const orbitR = t => RO + (RE + RM * 0.6 - RO) * decay(t);
    const orbitW = t => { const e = decay(t); return W0 * Math.pow(RO / orbitR(t), 1.5) * (1 + 3 * e * e); };
    const orbitA = t => { let a = W0 * t; if (t > TFALL) { const dt = (Math.min(t, TIMPACT) - TFALL) / 24; for (let i = 0; i < 24; i++) a += (orbitW(TFALL + (i + 0.5) * dt) - W0) * dt; } return a; };
    const moon = t => { const a = orbitA(t), R = orbitR(Math.min(t, TIMPACT)); return add(E, add(X.map(x => x * Math.cos(a) * R), Y.map(y => y * Math.sin(a) * R))); };
    const rodr = (v, k, a) => add(add(v.map(x => x * Math.cos(a)), cross(k, v).map(x => x * Math.sin(a))), k.map(x => x * dot(k, v) * (1 - Math.cos(a))));
    let seed = 7;
    const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    const S = 64, tex = new Float32Array(66 * 44 * 4), moonHits = [], earthHits = [];
    const camSide = norm([0.35, 0.45, 1]);
    for (let k = 0; k < 44; k++) {
      const t0 = 57.0 + k * 0.08 + rnd() * 0.12;
      // where it aims: most at the moon (which stands in the way), some at the earth, some only near
      const pick = rnd(), flight = 1.0 + rnd() * 0.4;
      let aim;
      const mAt = moon(t0 + flight);
      if (pick < 0.5) aim = add(mAt, [rnd() - 0.5, rnd() - 0.5, rnd() - 0.5].map(x => x * RM * 1.2));
      else if (pick < 0.78) aim = add(E, norm([rnd() - 0.5, rnd() - 0.5, rnd() - 0.5]), RE * 0.8);
      else aim = add(E, norm([rnd() - 0.5, rnd() - 0.5, rnd() - 0.5]), RE * (1.7 + rnd() * 1.4));
      const from = norm(add(add(norm(add(mAt, E, -1)), camSide), [rnd() - 0.5, rnd() - 0.5, rnd() - 0.5], 0.5));
      let p = add(aim, from, 13);
      // launch speed chosen so it gets there in about `flight` seconds (gravity will hurry it a little)
      let v = add(aim, p, -1).map(x => x / flight);
      const big = rnd() < 0.35 ? 1 : 0;
      let t = t0, kind = 0, hitN = [0, 0, 0], tEnd = t0 + 3;
      const path = [p.slice()], times = [t];
      const dt = 1 / 240;
      while (t < t0 + 3) {
        const r = add(p, E, -1), d = Math.hypot(...r);
        v = add(v, r, -GM / (d * d * d) * dt);
        p = add(p, v, dt); t += dt;
        path.push(p.slice()); times.push(t);
        const m = moon(t), dm = Math.hypot(...add(p, m, -1));
        if (dm < RM) { kind = 2; hitN = norm(add(p, m, -1)); tEnd = t; break; }
        if (Math.hypot(...add(p, E, -1)) < RE) { kind = 1; hitN = norm(add(p, E, -1)); tEnd = t; break; }
        if (Math.hypot(...add(p, E, -1)) > 18) { tEnd = t; break; }
      }
      // into the surface's own frame, so the crater turns with it
      let local;
      if (kind === 2) local = rodr(hitN, N, -orbitA(tEnd));
      else { const a = tEnd * 0.1, c = Math.cos(a), s = Math.sin(a); local = [c * hitN[0] - s * hitN[2], hitN[1], s * hitN[0] + c * hitN[2]]; }
      const dS = (tEnd - t0) / (S - 1);
      for (let i = 0; i < S; i++) {
        const tt = t0 + i * dS;
        let j = 0; while (j < times.length - 2 && times[j + 1] < tt) j++;
        const f = Math.min(1, Math.max(0, (tt - times[j]) / (times[j + 1] - times[j] || 1)));
        const o = (k * 66 + i) * 4;
        for (let c = 0; c < 3; c++) tex[o + c] = path[j][c] + (path[j + 1][c] - path[j][c]) * f;
      }
      tex.set([t0, dS, tEnd, kind], (k * 66 + 64) * 4);
      tex.set([local[0], local[1], local[2], big], (k * 66 + 65) * 4);
      if (kind === 2 && moonHits.length < 24) moonHits.push([...local, big ? -tEnd : tEnd]);
      if (kind === 1 && earthHits.length < 16) earthHits.push([...local, big ? -tEnd : tEnd]);
    }
    const dt = new THREE.DataTexture(tex, 66, 44, THREE.RGBAFormat, THREE.FloatType);
    dt.minFilter = dt.magFilter = THREE.NearestFilter; dt.needsUpdate = true;
    U.uMet.value = dt;
    const pack = (list, n) => { const a = new Float32Array(n * 4); for (let i = 0; i < n; i++) a.set(list[i] || [0, 1, 0, 1e4], i * 4); return a; };
    U.uMoonHit.value = pack(moonHits, 24); U.uEarthHit.value = pack(earthHits, 16);
    G.meteors = { moon: moonHits.length, earth: earthHits.length, miss: 44 - moonHits.length - earthHits.length };
  }

  // ---- the girl of the ending (js/girl.js): points traced from the reference portrait, placed so its flower is ours
  function buildGirl(U) {
    const D = window.HANA_GIRL;
    if (!D) return;
    const bin = atob(D.data), n = D.n, Wd = 256, Ht = Math.ceil(n / Wd);
    const b = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
    const t1 = new Float32Array(Wd * Ht * 4), t2 = new Float32Array(Wd * Ht * 4);
    const [FX, FY, FR] = D.flower, SC = 1.95 / (FR - 15);   // her flower's petals match our plumeria
    for (let i = 0; i < n; i++) {
      const o = i * 12;
      const x = b[o] | b[o + 1] << 8, y = b[o + 2] | b[o + 3] << 8, u = (b[o + 4] | b[o + 5] << 8) / 65535;
      t1.set([(x - FX) * SC, -(y - FY) * SC, u, b[o + 6]], i * 4);
      t2.set([b[o + 7] / 255, b[o + 8] / 255, b[o + 9] / 255, b[o + 10] / 255], i * 4);
    }
    const tex = a => { const t = new THREE.DataTexture(a, Wd, Ht, THREE.RGBAFormat, THREE.FloatType); t.minFilter = t.magFilter = THREE.NearestFilter; t.needsUpdate = true; return t; };
    U.uGirl1.value = tex(t1); U.uGirl2.value = tex(t2); U.uGirlN.value = n; U.uGirlH.value = Ht;
    const wx = x => (x - FX) * SC, wy = y => -(y - FY) * SC;
    U.uGirlHead.value.set(wx(D.head[0]), wy(D.head[1]), wx(D.neck[0]), wy(D.neck[1]));
    G.girl = { n, sc: SC, centre: [(D.w / 2 - FX) * SC, -(D.h / 2 - FY) * SC], height: D.h * SC };
  }

  const GLYPHS = {};
  function glyphFor(form) {
    const spec = form === 6 ? ['written.ink', 10, 4.2] : form === 10 ? ['written.olive', 5.4, 5.0] : form === 17 ? ['written.ink2', 9.5, 3.8, true] : null;
    if (!spec) return null;
    return GLYPHS[form] || (GLYPHS[form] = H.glyph(H.text(spec[0]) || ' ', spec[1], spec[2], spec[3]));
  }

  G.render = function (st) {
    const U = G.U, cam = G.camera;
    cam.fov = st.cam.fov;
    cam.position.set(st.cam.p[0], st.cam.p[1], st.cam.p[2]);
    cam.up.set(0, 1, 0);
    cam.lookAt(st.cam.q[0], st.cam.q[1], st.cam.q[2]);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
    G.sky.position.copy(cam.position);

    U.uA.value = st.A; U.uB.value = st.B; U.uMix.value = st.k;
    const gl = glyphFor(st.A) || glyphFor(st.B);
    if (gl) U.uGlyph.value = gl;
    U.uTA.value = st.tA; U.uTB.value = st.tB; U.uClock.value = st.clock;
    U.uLvl.value = st.f.lvl; U.uOn.value = st.f.on; U.uBeat.value = st.beat;
    U.uBand.value.set(st.f.low, st.f.lmid, st.f.hmid, st.f.air);
    U.uSeed.value.set(st.seed[0], st.seed[1], st.seed[2], 0);
    U.uHold.value = st.hold;
    U.uPx.value = G.H / (2 * Math.tan(cam.fov * Math.PI / 360));
    U.uDust.value = st.dust;

    const S = G.skyU, sk = st.sky;
    S.uZen.value.fromArray(sk.z); S.uHor.value.fromArray(sk.h);
    S.uSunDir.value.fromArray(sk.s); S.uSunCol.value.fromArray(sk.c);
    S.uSunI.value = sk.i; S.uStars.value = sk.st; S.uT.value = st.clock;
    S.uStorm.value = sk.storm; S.uOn.value = st.f.on; S.uAur.value = sk.aur; S.uHigh.value = st.f.air;

    G.bloom.strength = 0.75 + 0.35 * st.f.lvl;
    G.grade.uniforms.uT.value = st.clock;
    G.grade.uniforms.uExp.value = st.exposure;
    G.composer.render();
  };

})();
